import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { Play, Menu, X, Home, Grid, Crown, Settings, LogOut, User, TrendingUp, Users, CreditCard, Trash2, Shield, RefreshCw, Check, ChevronRight, ChevronDown, Eye, Clock, Star, Zap, Search, Lock, Flame, Mic, Music, Gamepad2, Tv, BookOpen, Dumbbell, ChefHat, Plane, FlaskConical, Briefcase, Heart, Film, Sparkles, Bell, Trophy, Bookmark, Plus, BarChart3, FileText, Edit3, Save, XCircle, Calendar, Target, Award, MessageSquare, ArrowLeft } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("smarttube_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(res.data);
      } catch (e) {
        localStorage.removeItem("smarttube_token");
        setToken(null);
      }
      setLoading(false);
    };

    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("smarttube_token", res.data.access_token);
    setToken(res.data.access_token);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (email, password, name) => {
    const res = await axios.post(`${API}/auth/register`, { email, password, name });
    localStorage.setItem("smarttube_token", res.data.access_token);
    setToken(res.data.access_token);
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("smarttube_token");
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const res = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(res.data);
      } catch (e) {
        // Silent fail on refresh
      }
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, ownerOnly = false }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" />;
  if (ownerOnly && !user.is_owner) return <Navigate to="/home" />;
  return children;
};

// Loading Screen
const LoadingScreen = () => (
  <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-400">Laden...</p>
    </div>
  </div>
);

// ============== LANDING PAGE (FRONT) ==============
const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user) navigate("/home");
  }, [user, navigate]);

  const features = [
    { icon: Clock, title: "Bespaar Tijd", desc: "Geen eindeloos scrollen meer - wij selecteren het beste" },
    { icon: Star, title: "100% Nederlands", desc: "De beste Nederlandse content, alles op één plek" },
    { icon: TrendingUp, title: "Kijk Nu", desc: "Real-time trending content met hoge kijkcijfers" },
    { icon: Zap, title: "Gepersonaliseerd", desc: "SmartTube leert jouw smaak en toont wat JIJ wilt" },
    { icon: Grid, title: "31 Categorieën", desc: "Van gaming tot podcasts, netjes georganiseerd" },
    { icon: Mic, title: "Populaire Kanalen", desc: "Ontdek trending creators met 100K+ volgers" }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-red-600/20 via-transparent to-transparent pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-6 py-8">
          <nav className="flex items-center justify-between mb-16">
            <h1 className="font-display text-2xl font-bold">
              Smart<span className="text-red-600">Tube</span>
            </h1>
            <div className="flex gap-4">
              <button onClick={() => navigate("/login")} className="px-6 py-2 text-gray-300 hover:text-white transition" data-testid="login-nav-btn">
                Inloggen
              </button>
              <button onClick={() => navigate("/register")} className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-full font-semibold transition" data-testid="register-nav-btn">
                Start Gratis
              </button>
            </div>
          </nav>

          <div className="text-center max-w-4xl mx-auto py-16">
            <div className="inline-block bg-gradient-to-r from-yellow-500 to-orange-500 text-black px-4 py-1 rounded-full text-sm font-bold mb-6">
              🎬 JOUW PERSOONLIJKE VIDEO GIDS
            </div>
            <h2 className="font-display text-5xl md:text-7xl font-bold mb-6 leading-tight">
              Ontdek de Beste <br />
              <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">YouTube Content</span>
            </h2>
            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto">
              Gecureerde, georganiseerde, gepersonaliseerde video content. 
              Niet meer verdwalen in de chaos van YouTube - wij brengen orde.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <button onClick={() => navigate("/register")} className="px-10 py-4 bg-red-600 hover:bg-red-700 text-white rounded-full text-lg font-semibold shadow-lg shadow-red-600/30 transition transform hover:scale-105" data-testid="get-started-btn">
                Start Gratis <ChevronRight className="inline ml-2" size={20} />
              </button>
              <button onClick={() => navigate("/login")} className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white rounded-full text-lg backdrop-blur transition" data-testid="login-hero-btn">
                Ik heb al een account
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="max-w-7xl mx-auto px-6 py-20">
        <h3 className="font-display text-3xl font-bold text-center mb-4">🔥 Waarom Je SmartTube Nodig Hebt</h3>
        <p className="text-gray-400 text-center mb-12">Jouw persoonlijke gids voor de beste YouTube video's</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <div key={i} className="bg-[#141414] border border-white/5 rounded-2xl p-6 hover:border-red-600/50 transition group">
              <div className="w-12 h-12 rounded-xl bg-red-600/10 flex items-center justify-center mb-4 group-hover:bg-red-600/20 transition">
                <f.icon className="text-red-500" size={24} />
              </div>
              <h4 className="font-display text-lg font-semibold mb-2">{f.title}</h4>
              <p className="text-gray-400 text-sm">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Pricing */}
      <div className="max-w-7xl mx-auto px-6 py-20">
        <h3 className="font-display text-3xl font-bold text-center mb-4">Kies je Plan</h3>
        <p className="text-center text-gray-400 mb-12">Automatisch doorlopend abonnement - Op elk moment opzegbaar</p>
        <div className="grid md:grid-cols-4 gap-4 max-w-6xl mx-auto items-start">
          {/* Gratis */}
          <div className="bg-[#141414] border border-white/10 rounded-2xl p-6">
            <h4 className="font-display text-xl font-semibold mb-2">Gratis</h4>
            <p className="text-3xl font-bold mb-4">€0<span className="text-lg text-gray-400">/maand</span></p>
            <ul className="space-y-2 mb-6">
              {["Scrollen door videos", "31 categorieën", "50+ videos/categorie", "Zoekfunctie"].map((item, i) => (
                <li key={i} className="flex items-center gap-2 text-gray-400 text-sm">
                  <Check size={14} className="text-gray-500" /> {item}
                </li>
              ))}
              <li className="flex items-center gap-2 text-red-400 text-sm">
                <X size={14} /> Videos kijken
              </li>
            </ul>
            <button onClick={() => navigate("/register")} className="w-full py-2.5 bg-white/10 hover:bg-white/20 rounded-full text-white text-sm transition">
              Gratis Starten
            </button>
          </div>
          
          {/* Maandelijks Premium */}
          <div className="bg-gradient-to-b from-red-600/20 to-[#141414] border border-red-600/50 rounded-2xl p-6 relative">
            <div className="absolute -top-3 right-4 bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-1 rounded-full text-xs font-bold">
              PREMIUM
            </div>
            <h4 className="font-display text-xl font-semibold mb-2">Premium</h4>
            <p className="text-3xl font-bold mb-4">€4,99<span className="text-lg text-gray-400">/maand</span></p>
            <ul className="space-y-2 mb-6">
              <li className="flex items-center gap-2 text-gray-300 text-sm">
                <Check size={14} className="text-green-500" /> Alles van Gratis
              </li>
              <li className="flex items-center gap-2 text-white text-sm font-medium">
                <Check size={14} className="text-green-500" /> ✨ Videos kijken
              </li>
              <li className="flex items-center gap-2 text-white text-sm font-medium">
                <Check size={14} className="text-green-500" /> ✨ 50+ videos/categorie
              </li>
              <li className="flex items-center gap-2 text-white text-sm font-medium">
                <Check size={14} className="text-green-500" /> ✨ Videos opslaan
              </li>
            </ul>
            <button onClick={() => navigate("/register")} className="w-full py-2.5 bg-red-600 hover:bg-red-700 rounded-full text-white text-sm font-semibold shadow-lg shadow-red-600/30 transition">
              Start Premium
            </button>
            <p className="text-center text-gray-500 text-xs mt-3">Automatisch verlengd</p>
          </div>
          
          {/* Platinum - BESTE DEAL */}
          <div className="bg-gradient-to-b from-purple-600/30 to-[#141414] border-2 border-purple-500 rounded-2xl p-6 relative transform scale-105">
            <div className="absolute -top-3 right-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-full text-xs font-bold animate-pulse">
              💎 PLATINUM
            </div>
            <h4 className="font-display text-xl font-semibold mb-2 text-purple-300">Platinum</h4>
            <p className="text-3xl font-bold mb-4">€9,99<span className="text-lg text-gray-400">/maand</span></p>
            <ul className="space-y-2 mb-6">
              <li className="flex items-center gap-2 text-gray-300 text-sm">
                <Check size={14} className="text-purple-400" /> Alles van Premium
              </li>
              <li className="flex items-center gap-2 text-purple-300 text-sm font-medium">
                <Check size={14} className="text-purple-400" /> 💎 100+ videos/categorie
              </li>
              <li className="flex items-center gap-2 text-purple-300 text-sm font-medium">
                <Check size={14} className="text-purple-400" /> 💎 Dubbele content
              </li>
              <li className="flex items-center gap-2 text-purple-300 text-sm font-medium">
                <Check size={14} className="text-purple-400" /> 💎 Prioriteit support
              </li>
            </ul>
            <button onClick={() => navigate("/register")} className="w-full py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 rounded-full text-white text-sm font-semibold shadow-lg shadow-purple-500/30 transition">
              Start Platinum
            </button>
            <p className="text-center text-gray-500 text-xs mt-3">Automatisch verlengd</p>
          </div>
          
          {/* Jaarlijks Premium */}
          <div className="bg-gradient-to-b from-yellow-600/20 to-[#141414] border border-yellow-500/50 rounded-2xl p-6 relative">
            <div className="absolute -top-3 right-4 bg-gradient-to-r from-yellow-500 to-amber-500 text-black px-3 py-1 rounded-full text-xs font-bold">
              BESTE DEAL
            </div>
            <h4 className="font-display text-xl font-semibold mb-2">Premium Jaar</h4>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-lg text-gray-500 line-through">€59,99</span>
              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded">-33%</span>
            </div>
            <p className="text-3xl font-bold mb-1">€39,99<span className="text-lg text-gray-400">/jaar</span></p>
            <p className="text-yellow-400 text-sm mb-4">= €3,33/maand</p>
            <ul className="space-y-2 mb-6">
              <li className="flex items-center gap-2 text-gray-300 text-sm">
                <Check size={14} className="text-yellow-500" /> Alles van Premium
              </li>
              <li className="flex items-center gap-2 text-yellow-400 text-sm font-medium">
                <Check size={14} className="text-yellow-500" /> 💰 Bespaar €19,89/jaar
              </li>
              <li className="flex items-center gap-2 text-white text-sm font-medium">
                <Check size={14} className="text-yellow-500" /> 12 maanden toegang
              </li>
            </ul>
            <button onClick={() => navigate("/register")} className="w-full py-2.5 bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 rounded-full text-black text-sm font-semibold shadow-lg shadow-yellow-500/30 transition">
              Start Jaarlijks
            </button>
            <p className="text-center text-gray-500 text-xs mt-3">Automatisch verlengd</p>
          </div>
          
          {/* Platinum Jaar - ULTIEME DEAL */}
          <div className="bg-gradient-to-b from-purple-600/30 to-[#141414] border-2 border-purple-500/50 rounded-2xl p-6 relative">
            <div className="absolute -top-3 right-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-full text-xs font-bold">
              💎 ULTIEM
            </div>
            <h4 className="font-display text-xl font-semibold mb-2 text-purple-300">Platinum Jaar</h4>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-lg text-gray-500 line-through">€119,99</span>
              <span className="bg-purple-500 text-white text-xs px-2 py-0.5 rounded">-33%</span>
            </div>
            <p className="text-3xl font-bold mb-1 text-purple-300">€79,99<span className="text-lg text-gray-400">/jaar</span></p>
            <p className="text-purple-400 text-sm mb-4">= €6,67/maand</p>
            <ul className="space-y-2 mb-6">
              <li className="flex items-center gap-2 text-gray-300 text-sm">
                <Check size={14} className="text-purple-400" /> Alles van Platinum
              </li>
              <li className="flex items-center gap-2 text-purple-300 text-sm font-medium">
                <Check size={14} className="text-purple-400" /> 💰 Bespaar €39,89/jaar
              </li>
              <li className="flex items-center gap-2 text-white text-sm font-medium">
                <Check size={14} className="text-purple-400" /> 12 maanden toegang
              </li>
              <li className="flex items-center gap-2 text-purple-300 text-sm font-medium">
                <Check size={14} className="text-purple-400" /> 💎 VIP Support
              </li>
            </ul>
            <button onClick={() => navigate("/register")} className="w-full py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 rounded-full text-white text-sm font-semibold shadow-lg shadow-purple-500/30 transition">
              Start Platinum Jaar
            </button>
            <p className="text-center text-gray-500 text-xs mt-3">Automatisch verlengd</p>
          </div>
        </div>
      </div>

      {/* Contact */}
      <div className="max-w-4xl mx-auto px-6 py-16 text-center">
        <h3 className="font-display text-2xl font-bold mb-4">Vragen?</h3>
        <p className="text-gray-400 mb-4">Log in en stuur ons een bericht via de app</p>
        <button onClick={() => navigate("/login")} className="inline-flex items-center gap-2 text-red-500 hover:text-red-400 transition">
          <span className="text-xl">💬</span> Contact via Support
        </button>
      </div>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500">© 2025 SmartTube. Alle rechten voorbehouden.</p>
            <div className="flex gap-6 text-sm">
              <button onClick={() => navigate("/privacy")} className="text-gray-400 hover:text-white transition">Privacy Policy</button>
              <button onClick={() => navigate("/voorwaarden")} className="text-gray-400 hover:text-white transition">Algemene Voorwaarden</button>
              <button onClick={() => navigate("/login")} className="text-gray-400 hover:text-white transition">Contact</button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// ============== LOGIN PAGE ==============
const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/home");
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/home");
    } catch (e) {
      setError(e.response?.data?.detail || "Inloggen mislukt");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="font-display text-3xl font-bold cursor-pointer" onClick={() => navigate("/")}>
            Smart<span className="text-red-600">Tube</span>
          </h1>
          <p className="text-gray-400 mt-2">Welkom terug!</p>
        </div>
        
        <form onSubmit={handleSubmit} className="bg-[#141414] border border-white/10 rounded-2xl p-8 space-y-6" data-testid="login-form">
          {error && (
            <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg text-sm" data-testid="login-error">
              {error}
            </div>
          )}
          
          <div>
            <label className="block text-sm text-gray-400 mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-gray-600 focus:border-red-600 focus:outline-none transition"
              placeholder="jouw@email.nl"
              required
              data-testid="login-email-input"
            />
          </div>
          
          <div>
            <label className="block text-sm text-gray-400 mb-2">Wachtwoord</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-gray-600 focus:border-red-600 focus:outline-none transition"
              placeholder="••••••••"
              required
              data-testid="login-password-input"
            />
          </div>
          
          <button type="submit" className="w-full py-3 bg-red-600 hover:bg-red-700 rounded-full text-white font-semibold transition" disabled={loading} data-testid="login-submit-btn">
            {loading ? "Laden..." : "Inloggen"}
          </button>
          
          <p className="text-center text-gray-400 text-sm">
            Nog geen account?{" "}
            <span onClick={() => navigate("/register")} className="text-red-500 cursor-pointer hover:underline">
              Registreer hier
            </span>
          </p>
        </form>
      </div>
    </div>
  );
};

// ============== REGISTER PAGE ==============
const RegisterPage = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [registeredEmail, setRegisteredEmail] = useState("");
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/home");
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register`, { email, password, name });
      if (res.data.requires_verification) {
        setRegisteredEmail(res.data.email);
        setShowVerification(true);
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Registratie mislukt");
    }
    setLoading(false);
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/verify-email`, { 
        email: registeredEmail, 
        code: verificationCode 
      });
      localStorage.setItem("smarttube_token", res.data.access_token);
      window.location.href = "/home";
    } catch (e) {
      setError(e.response?.data?.detail || "Verificatie mislukt");
    }
    setLoading(false);
  };

  const handleResendCode = async () => {
    setError("");
    try {
      await axios.post(`${API}/auth/resend-code`, { email: registeredEmail });
      alert("Nieuwe code verstuurd naar je email!");
    } catch (e) {
      setError(e.response?.data?.detail || "Code versturen mislukt");
    }
  };

  // Verification Screen
  if (showVerification) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="font-display text-3xl font-bold cursor-pointer" onClick={() => navigate("/")}>
              Smart<span className="text-red-600">Tube</span>
            </h1>
            <p className="text-gray-400 mt-2">Verifieer je email</p>
          </div>
          
          <form onSubmit={handleVerify} className="bg-[#141414] border border-white/10 rounded-2xl p-8 space-y-6" data-testid="verify-form">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="text-red-500" size={32} />
              </div>
              <h2 className="text-xl font-semibold mb-2">Check je inbox!</h2>
              <p className="text-gray-400 text-sm">
                We hebben een 6-cijferige code verstuurd naar<br />
                <span className="text-white font-medium">{registeredEmail}</span>
              </p>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg text-sm" data-testid="verify-error">
                {error}
              </div>
            )}
            
            <div>
              <label className="block text-sm text-gray-400 mb-2 text-center">Verificatiecode</label>
              <input 
                type="text" 
                value={verificationCode} 
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))} 
                className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-4 text-white text-center text-2xl tracking-[0.5em] placeholder:text-gray-600 focus:border-red-600 focus:outline-none transition" 
                placeholder="000000" 
                required 
                maxLength={6}
                data-testid="verify-code-input" 
              />
            </div>
            
            <button type="submit" className="w-full py-3 bg-red-600 hover:bg-red-700 rounded-full text-white font-semibold transition" disabled={loading || verificationCode.length !== 6} data-testid="verify-submit-btn">
              {loading ? "Verifiëren..." : "Verifieer Email"}
            </button>
            
            <div className="text-center">
              <p className="text-gray-500 text-sm mb-2">Geen code ontvangen?</p>
              <button type="button" onClick={handleResendCode} className="text-red-500 hover:underline text-sm" data-testid="resend-code-btn">
                Verstuur nieuwe code
              </button>
            </div>

            <p className="text-center text-gray-500 text-xs">
              Code is 15 minuten geldig
            </p>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="font-display text-3xl font-bold cursor-pointer" onClick={() => navigate("/")}>
            Smart<span className="text-red-600">Tube</span>
          </h1>
          <p className="text-gray-400 mt-2">Maak een gratis account</p>
        </div>

        {/* Info balkje over personalisatie */}
        <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/10 border border-yellow-500/30 rounded-xl p-4 mb-6 flex items-start gap-3">
          <div className="text-2xl">💡</div>
          <div>
            <p className="text-yellow-400 font-semibold text-sm">Tip voor snellere personalisatie!</p>
            <p className="text-gray-300 text-xs mt-1">Gebruik SmartTube's zoekfunctie om videos te vinden. Hoe meer je zoekt en kijkt, hoe beter de app jouw smaak leert kennen!</p>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="bg-[#141414] border border-white/10 rounded-2xl p-8 space-y-6" data-testid="register-form">
          {error && (
            <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg text-sm" data-testid="register-error">
              {error}
            </div>
          )}
          
          <div>
            <label className="block text-sm text-gray-400 mb-2">Naam</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-gray-600 focus:border-red-600 focus:outline-none transition" placeholder="Je naam" required data-testid="register-name-input" />
          </div>
          
          <div>
            <label className="block text-sm text-gray-400 mb-2">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-gray-600 focus:border-red-600 focus:outline-none transition" placeholder="jouw@email.nl" required data-testid="register-email-input" />
          </div>
          
          <div>
            <label className="block text-sm text-gray-400 mb-2">Wachtwoord</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-gray-600 focus:border-red-600 focus:outline-none transition" placeholder="••••••••" required minLength={6} data-testid="register-password-input" />
          </div>
          
          <button type="submit" className="w-full py-3 bg-red-600 hover:bg-red-700 rounded-full text-white font-semibold transition" disabled={loading} data-testid="register-submit-btn">
            {loading ? "Laden..." : "Account Aanmaken"}
          </button>
          
          <p className="text-center text-gray-400 text-sm">
            Al een account?{" "}
            <span onClick={() => navigate("/login")} className="text-red-500 cursor-pointer hover:underline">
              Log hier in
            </span>
          </p>
        </form>
      </div>
    </div>
  );
};

// ============== SMARTTUBE HOME (MAIN APP) ==============
const SmartTubeHome = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [popularChannels, setPopularChannels] = useState([]);
  const [trendingVideos, setTrendingVideos] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryVideos, setCategoryVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [savedVideos, setSavedVideos] = useState([]);
  const [showSavedVideos, setShowSavedVideos] = useState(false);
  const [savingVideo, setSavingVideo] = useState(null);
  const [region, setRegion] = useState("nl"); // "nl" = Nederlands, "us" = Amerikaans
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);

  const [voetbalOpen, setVoetbalOpen] = useState(false);
  
  // Statistics state
  const [showStatistics, setShowStatistics] = useState(false);
  const [statistics, setStatistics] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [expandedStatCard, setExpandedStatCard] = useState(null); // Track which stat card is expanded
  
  // Notes state
  const [showNotes, setShowNotes] = useState(false);
  const [userNotes, setUserNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [activeNoteVideo, setActiveNoteVideo] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  
  // In-App Video Player state
  const [activeVideo, setActiveVideo] = useState(null);

  // Fetch main data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, channelsRes, trendingRes] = await Promise.all([
          axios.get(`${API}/categories?region=${region}`),
          axios.get(`${API}/channels/popular?region=${region}`),
          axios.get(`${API}/videos/trending?region=${region}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
        ]);
        setCategories(catRes.data);
        setPopularChannels(channelsRes.data);
        setTrendingVideos(trendingRes.data.videos || []);
      } catch (e) {
        console.error('Failed to fetch main data:', e.message);
      }
      setLoading(false);
    };

    const fetchSavedVideosData = async () => {
      try {
        const res = await axios.get(`${API}/saved-videos`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSavedVideos(res.data.videos || []);
      } catch (e) {
        console.error('Failed to fetch saved videos:', e.message);
      }
    };

    const checkPushStatusData = async () => {
      try {
        const res = await axios.get(`${API}/push/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPushEnabled(res.data.enabled);
      } catch (e) {
        console.error('Failed to check push status:', e.message);
      }
    };

    fetchData();
    if (user?.is_premium && token) {
      fetchSavedVideosData();
      checkPushStatusData();
    }
  }, [region, token, user?.is_premium]);

  // Session tracking - heartbeat elke 5 minuten
  useEffect(() => {
    if (!token) return;
    
    const heartbeatInterval = setInterval(async () => {
      try {
        await axios.post(`${API}/session/heartbeat`, 
          { duration_minutes: 5 },
          { headers: { Authorization: `Bearer ${token}` }}
        );
      } catch (e) {
        console.error('Heartbeat failed:', e.message);
      }
    }, 5 * 60 * 1000); // Elke 5 minuten
    
    // Initiële heartbeat bij laden
    axios.post(`${API}/session/heartbeat`, 
      { duration_minutes: 1 },
      { headers: { Authorization: `Bearer ${token}` }}
    ).catch((e) => console.error('Initial heartbeat failed:', e.message));
    
    return () => clearInterval(heartbeatInterval);
  }, [token]);

  const fetchSavedVideos = async () => {
    try {
      const res = await axios.get(`${API}/saved-videos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedVideos(res.data.videos || []);
    } catch (e) {
      // Production: error logged
    }
  };

  const checkPushStatus = async () => {
    try {
      const res = await axios.get(`${API}/push/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPushEnabled(res.data.enabled);
    } catch (e) {
      // Production: error logged
    }
  };

  // Fetch Statistics
  const fetchStatistics = async () => {
    setStatsLoading(true);
    try {
      const res = await axios.get(`${API}/statistics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatistics(res.data);
    } catch (e) {
      // Production: error logged
    }
    setStatsLoading(false);
  };

  // Fetch Notes
  const fetchNotes = async () => {
    setNotesLoading(true);
    try {
      const res = await axios.get(`${API}/notes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserNotes(res.data.notes || []);
    } catch (e) {
      // Production: error logged
    }
    setNotesLoading(false);
  };

  // Save Note
  const saveNote = async (video) => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      await axios.post(`${API}/notes`, {
        video_id: video.id,
        video_title: video.title,
        video_thumbnail: video.thumbnail,
        video_channel: video.channel,
        note_text: noteText
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActiveNoteVideo(null);
      setNoteText("");
      fetchNotes();
    } catch (e) {
      alert("Kon notitie niet opslaan");
    }
    setSavingNote(false);
  };

  // Delete Note
  const deleteNote = async (videoId) => {
    if (!window.confirm("Weet je zeker dat je deze notitie wilt verwijderen?")) return;
    try {
      await axios.delete(`${API}/notes/${videoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserNotes(userNotes.filter(n => n.video_id !== videoId));
    } catch (e) {
      alert("Kon notitie niet verwijderen");
    }
  };

  // Load statistics when viewing
  useEffect(() => {
    if (showStatistics && token) {
      fetchStatistics();
    }
  }, [showStatistics, token]);

  // Load notes on mount and when viewing
  useEffect(() => {
    if (token) {
      fetchNotes();
    }
  }, [token]);

  useEffect(() => {
    if (showNotes && token) {
      fetchNotes();
    }
  }, [showNotes, token]);

  const togglePushNotifications = async () => {
    if (!user?.is_premium) {
      setShowPremiumModal(true);
      return;
    }
    
    setPushLoading(true);
    
    if (pushEnabled) {
      // Disable push
      try {
        await axios.delete(`${API}/push/unsubscribe`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPushEnabled(false);
      } catch (e) {
        // Production: error logged
      }
    } else {
      // Enable push
      try {
        // Register service worker
        const registration = await navigator.serviceWorker.register('/sw-push.js');
        
        // Get VAPID public key
        const keyRes = await axios.get(`${API}/push/vapid-public-key`);
        const vapidPublicKey = keyRes.data.publicKey;
        
        // Convert VAPID key
        const urlBase64ToUint8Array = (base64String) => {
          const padding = '='.repeat((4 - base64String.length % 4) % 4);
          const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
          const rawData = window.atob(base64);
          const outputArray = new Uint8Array(rawData.length);
          for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
          }
          return outputArray;
        };
        
        // Subscribe to push
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
        });
        
        // Send subscription to backend
        await axios.post(`${API}/push/subscribe`, subscription.toJSON(), {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setPushEnabled(true);
      } catch (e) {
        // Production: error logged
        alert('Push notificaties konden niet worden ingeschakeld. Controleer je browser instellingen.');
      }
    }
    
    setPushLoading(false);
  };

  const loadCategory = async (catId) => {
    setSelectedCategory(catId);
    setSearchResults(null);
    setShowSavedVideos(false);
    try {
      const res = await axios.get(`${API}/videos/${catId}?region=${region}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setCategoryVideos(res.data.videos || []);
    } catch (e) {
      // Production: error logged
    }
  };

  const loadSavedVideos = () => {
    setShowSavedVideos(true);
    setSelectedCategory(null);
    setSearchResults(null);
    fetchSavedVideos();
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    
    // Direct naar YouTube doorsturen
    const youtubeSearchUrl = `https://www.youtube.com/results?search_query=${encodeURIComponent(searchQuery)}`;
    window.open(youtubeSearchUrl, '_blank');
    setSearchQuery(''); // Reset zoekbalk
  };

  const handleDeleteAccount = async () => {
    setDeletingAccount(true);
    try {
      await axios.delete(`${API}/account/delete`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Account is verwijderd, log uit en redirect
      logout();
      alert("Je account is succesvol verwijderd. Alle abonnementen zijn per direct geannuleerd.");
      navigate("/");
    } catch (e) {
      alert(e.response?.data?.detail || "Er is een fout opgetreden bij het verwijderen van je account.");
    }
    setDeletingAccount(false);
    setShowDeleteAccountModal(false);
  };

  const handleVideoClick = (video) => {
    if (!user?.is_premium) {
      setShowPremiumModal(true);
      return;
    }
    
    // Premium user can watch - Open in-app embedded player (NOT external YouTube)
    // Extract video ID from youtube_url if needed
    let videoId = video.id;
    if (video.youtube_url && !videoId) {
      const match = video.youtube_url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
      if (match) videoId = match[1];
    }
    
    if (videoId) {
      // Open in-app video player modal
      setActiveVideo({ ...video, id: videoId });
    }
    
    // Track watch for personalization (async)
    axios.post(`${API}/videos/watch`, {
      video_id: video.id || videoId,
      category_id: video.category_id,
      channel: video.channel,
      watch_duration: 0
    }, { headers: { Authorization: `Bearer ${token}` } }).catch(e => {
      // Production: error logged
    });
  };

  const handleSaveVideo = async (video, e) => {
    e.stopPropagation();
    if (!user?.is_premium) {
      setShowPremiumModal(true);
      return;
    }
    
    setSavingVideo(video.id);
    try {
      await axios.post(`${API}/saved-videos`, {
        video_id: video.id,
        title: video.title,
        thumbnail: video.thumbnail,
        channel: video.channel,
        duration: video.duration,
        category_id: video.category_id
      }, { headers: { Authorization: `Bearer ${token}` } });
      fetchSavedVideos();
      alert("Video opgeslagen!");
    } catch (e) {
      alert(e.response?.data?.detail || "Opslaan mislukt");
    }
    setSavingVideo(null);
  };

  const handleRemoveSavedVideo = async (videoId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/saved-videos/${videoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchSavedVideos();
    } catch (e) {
      // Production: error logged
    }
  };

  const getCategoryIcon = (catId) => {
    const icons = {
      "trending": Flame,
      "populair": Mic,
      "entertainment": Tv,
      "funny": Sparkles,
      "comedy": Sparkles,
      "voetbal": Star,
      "eredivisie": Star,
      "premier-league": Star,
      "la-liga": Star,
      "bundesliga": Star,
      "ligue1": Star,
      "serie-a": Star,
      "dutch-youtube": Star,
      "supergaande": Mic,
      "dutch-music": Music,
      "dutch-news": Tv,
      "gaming": Gamepad2,
      "music": Music,
      "movies": Film,
      "shorts": Play,
      "vlogs": Eye,
      "fitness": Dumbbell,
      "food": ChefHat,
      "travel": Plane,
      "tech": Tv,
      "education": BookOpen,
      "science": FlaskConical,
      "true-crime": Search,
      "documentaries": Film,
      "animals": Heart
    };
    return icons[catId] || Grid;
  };

  // Render video card with lock for free users and save button - COMPACT VERSION
  const VideoCard = ({ video, index, showRemove = false }) => {
    const canWatch = user?.is_premium;
    const isSaved = savedVideos.some(v => v.video_id === video.id);
    const hasNote = userNotes.some(n => n.video_id === video.id);
    
    return (
      <div 
        className="group cursor-pointer relative"
        onClick={() => handleVideoClick(video)}
        style={{ animationDelay: `${index * 0.02}s` }}
        data-testid={`video-${video.id}`}
      >
        <div className="relative aspect-video rounded-lg overflow-hidden bg-[#1a1a1a] mb-2">
          <img 
            src={video.thumbnail} 
            alt={video.title} 
            className="w-full h-full object-cover group-hover:scale-105 transition duration-300" 
            onError={(e) => { e.target.src = 'https://via.placeholder.com/480x360/1a1a1a/666?text=Video'; }}
          />
          
          {/* Play overlay */}
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
            {canWatch ? (
              <div className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center">
                <Play fill="white" size={18} />
              </div>
            ) : (
              <div className="w-10 h-10 rounded-full bg-gray-800/90 flex items-center justify-center">
                <Lock size={18} className="text-gray-400" />
              </div>
            )}
          </div>
          
          {/* Note button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setActiveNoteVideo(video);
              const existingNote = userNotes.find(n => n.video_id === video.id);
              setNoteText(existingNote ? existingNote.note_text : "");
            }}
            className={`absolute top-1.5 left-1.5 p-1.5 rounded-full transition opacity-0 group-hover:opacity-100 ${
              hasNote ? "bg-blue-500 text-white" : "bg-black/70 hover:bg-black/90 text-white"
            }`}
            data-testid={`note-video-${video.id}`}
          >
            <FileText size={14} />
          </button>
          
          {/* Save/Remove button (Premium) */}
          {canWatch && (
            <button
              onClick={(e) => showRemove ? handleRemoveSavedVideo(video.video_id || video.id, e) : handleSaveVideo(video, e)}
              className={`absolute top-1.5 right-1.5 p-1.5 rounded-full transition opacity-0 group-hover:opacity-100 ${
                showRemove ? "bg-red-600 hover:bg-red-700" : isSaved ? "bg-yellow-500" : "bg-black/70 hover:bg-black/90"
              }`}
              disabled={savingVideo === video.id}
              data-testid={`save-video-${video.id}`}
            >
              {showRemove ? <Trash2 size={14} /> : isSaved ? <Star size={14} fill="currentColor" /> : <Star size={14} />}
            </button>
          )}
          
          {/* Duration */}
          <div className="absolute bottom-1 right-1 bg-black/80 px-1.5 py-0.5 rounded text-xs">
            {video.duration}
          </div>
          
          {/* Lock badge for free users */}
          {!canWatch && (
            <div className="absolute top-1.5 right-1.5 bg-yellow-600/90 px-1.5 py-0.5 rounded text-xs font-semibold flex items-center gap-1">
              <Crown size={10} /> PRO
            </div>
          )}
          
          {/* Note indicator */}
          {hasNote && (
            <div className="absolute bottom-1 left-1 bg-blue-500/90 px-1.5 py-0.5 rounded text-xs font-semibold flex items-center gap-1">
              <FileText size={10} /> Notitie
            </div>
          )}
        </div>
        
        <h3 className="font-medium text-sm line-clamp-2 mb-0.5 group-hover:text-red-500 transition">{video.title}</h3>
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <span className="truncate">{video.channel}</span>
          <span>•</span>
          <span>{video.views || "0"}</span>
        </div>
      </div>
    );
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Mobile-First Header */}
      <header className="sticky top-0 z-50 bg-[#0a0a0a]/95 backdrop-blur border-b border-white/5">
        <div className="flex items-center gap-3 px-4 py-3">
          {/* Hamburger Menu Button */}
          <button 
            onClick={() => setSidebarOpen(true)} 
            className="p-2 hover:bg-white/10 rounded-lg transition"
            data-testid="mobile-menu-btn"
          >
            <Menu size={24} />
          </button>
          
          {/* Logo */}
          <h1 className="font-display text-lg font-bold flex-shrink-0">
            Smart<span className="text-red-600">Tube</span>
          </h1>
          
          {/* Search - Takes remaining space */}
          <form onSubmit={handleSearch} className="flex-1 min-w-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Zoeken..."
                className="w-full bg-[#1a1a1a] border border-white/10 rounded-full pl-10 pr-4 py-2 text-sm text-white placeholder:text-gray-500 focus:border-red-600 focus:outline-none transition"
                data-testid="search-input"
              />
            </div>
          </form>

          {/* ⭐ Saved Videos Button (Premium) */}
          {user?.is_premium && (
            <button
              onClick={loadSavedVideos}
              className={`p-2 rounded-full text-lg transition flex-shrink-0 ${showSavedVideos ? "bg-yellow-500 text-black" : "text-yellow-500 hover:bg-yellow-500/20"}`}
              data-testid="header-saved-btn"
              title="Opgeslagen videos"
            >
              ⭐
            </button>
          )}

          {/* Region Flags */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => setRegion("nl")}
              className={`p-1.5 rounded-full text-base transition ${region === "nl" ? "bg-red-600" : "hover:bg-white/10"}`}
              data-testid="region-nl-btn"
            >
              🇳🇱
            </button>
            <button
              onClick={() => setRegion("us")}
              className={`p-1.5 rounded-full text-base transition ${region === "us" ? "bg-blue-600" : "hover:bg-white/10"}`}
              data-testid="region-us-btn"
            >
              🇺🇸
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 bg-black/60" onClick={() => setSidebarOpen(false)}>
          <aside 
            className="absolute left-0 top-0 bottom-0 w-72 bg-[#0f0f0f] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Sidebar Header */}
            <div className="flex-shrink-0 p-4 flex items-center justify-between border-b border-white/10">
              <h2 className="font-display text-xl font-bold">
                Smart<span className="text-red-600">Tube</span>
              </h2>
              <button onClick={() => setSidebarOpen(false)} className="p-2 hover:bg-white/10 rounded-lg">
                <X size={24} />
              </button>
            </div>

            {/* User Info */}
            <div className="flex-shrink-0 p-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#1a1a1a] flex items-center justify-center">
                  <User size={18} className="text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{user?.name}</p>
                  {user?.is_premium ? (
                    <span className="text-xs bg-gradient-to-r from-yellow-500 to-orange-500 text-black px-2 py-0.5 rounded-full font-semibold">PREMIUM</span>
                  ) : (
                    <span className="text-xs text-gray-500">Gratis</span>
                  )}
                </div>
              </div>
              {!user?.is_premium && (
                <button 
                  onClick={() => { setSidebarOpen(false); navigate("/premium"); }} 
                  className="w-full mt-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-semibold transition"
                >
                  <Crown size={14} className="inline mr-1" /> Upgrade naar Premium
                </button>
              )}
            </div>

            {/* Scrollable Categories */}
            <div className="flex-1 overflow-y-auto">
              {/* 🇺🇸 USA Top 100 */}
              <div className="p-3 border-b border-white/10">
                <button
                  onClick={() => { setRegion("us"); loadCategory("trending"); setSidebarOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition ${region === "us" && selectedCategory === "trending" ? "bg-blue-600 text-white" : "bg-blue-600/10 text-blue-400 border border-blue-500/30"}`}
                  data-testid="usa-top100-btn"
                >
                  <span className="text-xl">🇺🇸</span>
                  <span className="font-medium">USA Top 100</span>
                </button>
              </div>

              {/* All 31 Categories */}
              <div className="p-3">
                <p className="text-xs text-gray-500 uppercase px-2 mb-2 font-semibold">📁 31 Categorieën</p>
                
                {/* Featured */}
                {categories.filter(c => c.group === "featured").map(cat => {
                  const Icon = getCategoryIcon(cat.id);
                  return (
                    <button
                      key={cat.id}
                      onClick={() => { loadCategory(cat.id); setSidebarOpen(false); }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition mb-1 ${selectedCategory === cat.id ? "bg-red-600 text-white" : "text-gray-300 hover:bg-white/5"}`}
                    >
                      <Icon size={18} />
                      <span className="text-sm">{cat.name}</span>
                    </button>
                  );
                })}

                {/* Voetbal Dropdown */}
                <div className="my-2">
                  <button
                    onClick={() => setVoetbalOpen(!voetbalOpen)}
                    className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left transition text-gray-300 hover:bg-white/5"
                  >
                    <div className="flex items-center gap-3">
                      <Trophy size={18} />
                      <span className="text-sm font-medium">⚽ Voetbal</span>
                    </div>
                    <ChevronDown size={16} className={`transition-transform ${voetbalOpen ? "rotate-180" : ""}`} />
                  </button>
                  {voetbalOpen && (
                    <div className="ml-6 mt-1 space-y-1 border-l-2 border-white/10 pl-3">
                      {categories.filter(c => c.group === "voetbal" || c.group === "voetbal-parent").map(cat => (
                        <button
                          key={cat.id}
                          onClick={() => { loadCategory(cat.id); setSidebarOpen(false); }}
                          className={`w-full px-3 py-2 rounded-lg text-left transition text-sm ${selectedCategory === cat.id ? "bg-red-600 text-white" : "text-gray-400 hover:bg-white/5"}`}
                        >
                          {cat.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* All other categories */}
                <div className="border-t border-white/10 pt-2 mt-2">
                  {categories.filter(c => !c.group || c.group === "" || c.group === null).map(cat => {
                    const Icon = getCategoryIcon(cat.id);
                    return (
                      <button
                        key={cat.id}
                        onClick={() => { loadCategory(cat.id); setSidebarOpen(false); }}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition mb-0.5 ${selectedCategory === cat.id ? "bg-red-600 text-white" : "text-gray-400 hover:bg-white/5"}`}
                      >
                        <Icon size={16} />
                        <span className="text-sm">{cat.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* STICKY BOTTOM: Statistieken, Notities, ⭐ Opgeslagen + Uitloggen */}
            <div className="flex-shrink-0 border-t border-white/10 bg-[#0f0f0f]">
              {/* 📊 Statistieken */}
              <div className="p-3 border-b border-white/10">
                <button
                  onClick={() => { setShowStatistics(true); setShowNotes(false); setShowSavedVideos(false); setSidebarOpen(false); }}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-left transition ${showStatistics ? "bg-green-500 text-black" : "bg-green-500/10 text-green-500 border border-green-500/30"}`}
                  data-testid="statistics-btn"
                >
                  <div className="flex items-center gap-3">
                    <BarChart3 size={20} />
                    <span className="font-medium">📊 Statistieken</span>
                  </div>
                </button>
              </div>

              {/* 📝 Notities */}
              <div className="p-3 border-b border-white/10">
                <button
                  onClick={() => { setShowNotes(true); setShowStatistics(false); setShowSavedVideos(false); setSidebarOpen(false); }}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-left transition ${showNotes ? "bg-blue-500 text-black" : "bg-blue-500/10 text-blue-500 border border-blue-500/30"}`}
                  data-testid="notes-btn"
                >
                  <div className="flex items-center gap-3">
                    <FileText size={20} />
                    <span className="font-medium">📝 Mijn Notities</span>
                  </div>
                  <span className={`text-sm px-2.5 py-1 rounded-full font-bold ${showNotes ? "bg-black/20" : "bg-blue-500/20"}`}>{userNotes.length}</span>
                </button>
              </div>

              {/* ⭐ Opgeslagen Videos */}
              {user?.is_premium && (
                <div className="p-3 border-b border-white/10">
                  <button
                    onClick={() => { loadSavedVideos(); setShowStatistics(false); setShowNotes(false); setSidebarOpen(false); }}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-left transition ${showSavedVideos ? "bg-yellow-500 text-black" : "bg-yellow-500/10 text-yellow-500 border border-yellow-500/30"}`}
                    data-testid="saved-videos-btn"
                  >
                    <div className="flex items-center gap-3">
                      <Star size={20} fill={showSavedVideos ? "currentColor" : "none"} />
                      <span className="font-medium">⭐ Opgeslagen</span>
                    </div>
                    <span className={`text-sm px-2.5 py-1 rounded-full font-bold ${showSavedVideos ? "bg-black/20" : "bg-yellow-500/20"}`}>{savedVideos.length}</span>
                  </button>
                </div>
              )}

              {/* Bottom Actions */}
              <div className="p-3 space-y-2">
                {/* Support Button */}
                <button 
                  onClick={() => { setSidebarOpen(false); navigate("/support"); }} 
                  className="w-full py-2.5 bg-white/5 hover:bg-white/10 rounded-lg text-sm transition flex items-center justify-center gap-2"
                >
                  <MessageSquare size={14} /> Support / Contact
                </button>
                
                <div className="flex gap-2">
                  {user?.is_owner && (
                    <button 
                      onClick={() => { setSidebarOpen(false); navigate("/admin"); }} 
                      className="flex-1 py-2.5 bg-cyan-600/20 text-cyan-400 rounded-lg text-sm font-medium"
                    >
                      <Shield size={14} className="inline mr-1" /> Admin
                    </button>
                  )}
                  <button 
                    onClick={() => { 
                      logout(); 
                      // Use window.location to ensure redirect to landing happens before ProtectedRoute kicks in
                      window.location.href = '/';
                    }} 
                    className="flex-1 py-2.5 bg-white/10 hover:bg-white/20 rounded-lg text-sm transition"
                    data-testid="logout-btn"
                  >
                    <LogOut size={14} className="inline mr-1" /> Uitloggen
                  </button>
                </div>
                
                {/* Abonnement Beheren - Alleen voor Premium users */}
                {user?.is_premium && (
                  <button 
                    onClick={async () => {
                      try {
                        const res = await axios.post(`${API}/stripe/portal`, {}, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        window.location.href = res.data.url;
                      } catch (e) {
                        alert("Kon abonnementbeheer niet openen. Probeer het later opnieuw.");
                      }
                    }} 
                    className="w-full py-2.5 bg-purple-600/20 hover:bg-purple-600/30 text-purple-400 border border-purple-500/30 rounded-lg text-sm transition flex items-center justify-center gap-2"
                    data-testid="manage-subscription-btn"
                  >
                    <CreditCard size={14} /> Abonnement Beheren
                  </button>
                )}
                
                {/* Account Verwijderen - Rood, alleen voor niet-owners */}
                {!user?.is_owner && (
                  <button 
                    onClick={() => setShowDeleteAccountModal(true)} 
                    className="w-full py-2.5 bg-red-600/20 hover:bg-red-600/30 text-red-500 border border-red-500/30 rounded-lg text-sm transition flex items-center justify-center gap-2"
                    data-testid="delete-account-btn"
                  >
                    <Trash2 size={14} /> Account Verwijderen
                  </button>
                )}
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className="px-3 py-4 pb-20">
          {/* Statistics Dashboard */}
          {showStatistics && (
            <div className="mb-10">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-display text-2xl font-bold flex items-center gap-2">
                    <BarChart3 className="text-green-500" size={24} /> Kijkstatistieken
                  </h2>
                  <p className="text-gray-400 text-sm mt-1">Jouw persoonlijke kijkgedrag</p>
                </div>
                <button onClick={() => setShowStatistics(false)} className="text-gray-400 hover:text-white">
                  <X size={20} />
                </button>
              </div>
              
              {statsLoading ? (
                <div className="flex items-center justify-center py-20">
                  <RefreshCw className="animate-spin text-green-500" size={32} />
                </div>
              ) : statistics ? (
                <div className="space-y-6">
                  {/* Streak Banner */}
                  {statistics.streak_days > 0 && (
                    <div className="bg-gradient-to-r from-orange-500/20 to-yellow-500/20 border border-orange-500/30 rounded-xl p-4 flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-r from-orange-500 to-yellow-500 flex items-center justify-center">
                        <Flame size={32} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-orange-400">{statistics.streak_days} dagen streak! 🔥</h3>
                        <p className="text-gray-400 text-sm">Je kijkt {statistics.streak_days} dagen achter elkaar</p>
                      </div>
                    </div>
                  )}

                  {/* Stats Grid - CLICKABLE */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {/* Today - Clickable */}
                    <div 
                      className={`bg-[#141414] border rounded-xl p-4 cursor-pointer transition-all hover:border-blue-500/50 ${expandedStatCard === 'today' ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-white/10'}`}
                      onClick={() => setExpandedStatCard(expandedStatCard === 'today' ? null : 'today')}
                      data-testid="stat-card-today"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar className="text-blue-400" size={18} />
                        <span className="text-gray-400 text-sm">Vandaag</span>
                        <ChevronDown className={`ml-auto text-gray-500 transition-transform ${expandedStatCard === 'today' ? 'rotate-180' : ''}`} size={16} />
                      </div>
                      <p className="text-3xl font-bold text-white">{statistics.today.videos_watched}</p>
                      <p className="text-gray-500 text-xs">{statistics.today.watch_time_minutes} min gekeken</p>
                    </div>

                    {/* This Week - Clickable */}
                    <div 
                      className={`bg-[#141414] border rounded-xl p-4 cursor-pointer transition-all hover:border-green-500/50 ${expandedStatCard === 'week' ? 'border-green-500 ring-2 ring-green-500/20' : 'border-white/10'}`}
                      onClick={() => setExpandedStatCard(expandedStatCard === 'week' ? null : 'week')}
                      data-testid="stat-card-week"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Target className="text-green-400" size={18} />
                        <span className="text-gray-400 text-sm">Deze Week</span>
                        <ChevronDown className={`ml-auto text-gray-500 transition-transform ${expandedStatCard === 'week' ? 'rotate-180' : ''}`} size={16} />
                      </div>
                      <p className="text-3xl font-bold text-white">{statistics.this_week.videos_watched}</p>
                      <p className="text-gray-500 text-xs">{statistics.this_week.watch_time_hours}u gekeken</p>
                    </div>

                    {/* This Month - Clickable */}
                    <div 
                      className={`bg-[#141414] border rounded-xl p-4 cursor-pointer transition-all hover:border-purple-500/50 ${expandedStatCard === 'month' ? 'border-purple-500 ring-2 ring-purple-500/20' : 'border-white/10'}`}
                      onClick={() => setExpandedStatCard(expandedStatCard === 'month' ? null : 'month')}
                      data-testid="stat-card-month"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Award className="text-purple-400" size={18} />
                        <span className="text-gray-400 text-sm">Deze Maand</span>
                        <ChevronDown className={`ml-auto text-gray-500 transition-transform ${expandedStatCard === 'month' ? 'rotate-180' : ''}`} size={16} />
                      </div>
                      <p className="text-3xl font-bold text-white">{statistics.this_month.videos_watched}</p>
                      <p className="text-gray-500 text-xs">{statistics.this_month.watch_time_hours}u gekeken</p>
                    </div>

                    {/* Total - Clickable */}
                    <div 
                      className={`bg-gradient-to-br from-red-600/20 to-orange-500/20 border rounded-xl p-4 cursor-pointer transition-all hover:border-yellow-500/50 ${expandedStatCard === 'total' ? 'border-yellow-500 ring-2 ring-yellow-500/20' : 'border-red-500/30'}`}
                      onClick={() => setExpandedStatCard(expandedStatCard === 'total' ? null : 'total')}
                      data-testid="stat-card-total"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Trophy className="text-yellow-400" size={18} />
                        <span className="text-gray-400 text-sm">Totaal</span>
                        <ChevronDown className={`ml-auto text-gray-500 transition-transform ${expandedStatCard === 'total' ? 'rotate-180' : ''}`} size={16} />
                      </div>
                      <p className="text-3xl font-bold text-white">{statistics.total.videos_watched}</p>
                      <p className="text-gray-500 text-xs">{statistics.total.watch_time_hours}u gekeken</p>
                    </div>
                  </div>

                  {/* Expanded Video List */}
                  {expandedStatCard && (
                    <div className="bg-[#141414] border border-white/10 rounded-xl p-4 animate-in slide-in-from-top-2 duration-200" data-testid="expanded-videos-list">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-semibold text-white flex items-center gap-2">
                          {expandedStatCard === 'today' && <><Calendar className="text-blue-400" size={18} /> Videos van vandaag</>}
                          {expandedStatCard === 'week' && <><Target className="text-green-400" size={18} /> Videos deze week</>}
                          {expandedStatCard === 'month' && <><Award className="text-purple-400" size={18} /> Videos deze maand</>}
                          {expandedStatCard === 'total' && <><Trophy className="text-yellow-400" size={18} /> Alle bekeken videos</>}
                        </h4>
                        <button 
                          onClick={() => setExpandedStatCard(null)}
                          className="text-gray-500 hover:text-white transition"
                        >
                          <X size={18} />
                        </button>
                      </div>
                      
                      {(() => {
                        const videos = expandedStatCard === 'today' ? statistics.today.videos :
                                       expandedStatCard === 'week' ? statistics.this_week.videos :
                                       expandedStatCard === 'month' ? statistics.this_month.videos :
                                       statistics.total.videos;
                        
                        if (!videos || videos.length === 0) {
                          return (
                            <div className="text-center py-8 text-gray-500">
                              <Play className="mx-auto mb-2 opacity-50" size={32} />
                              <p>Geen videos in deze periode</p>
                            </div>
                          );
                        }
                        
                        return (
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-80 overflow-y-auto">
                            {videos.map((video, idx) => (
                              <div 
                                key={`${video.id}-${idx}`}
                                className="group cursor-pointer"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (user?.is_premium) {
                                    setActiveVideo({ ...video, id: video.id });
                                  } else {
                                    setShowPremiumModal(true);
                                  }
                                }}
                              >
                                <div className="relative aspect-video rounded-lg overflow-hidden bg-black/50 mb-2">
                                  {video.thumbnail ? (
                                    <img 
                                      src={video.thumbnail} 
                                      alt={video.title}
                                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center bg-gray-800">
                                      <Play className="text-gray-600" size={24} />
                                    </div>
                                  )}
                                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition flex items-center justify-center opacity-0 group-hover:opacity-100">
                                    <Play className="text-white" size={24} />
                                  </div>
                                </div>
                                <p className="text-xs text-white font-medium line-clamp-2 group-hover:text-red-400 transition">{video.title}</p>
                                <p className="text-xs text-gray-500 truncate">{video.channel}</p>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Favorite Categories */}
                  {statistics.favorite_categories && statistics.favorite_categories.length > 0 ? (
                    <div className="bg-[#141414] border border-white/10 rounded-xl p-5">
                      <h3 className="font-semibold mb-4 flex items-center gap-2">
                        <Heart className="text-red-500" size={18} /> Favoriete Categorieën
                      </h3>
                      <div className="space-y-3">
                        {statistics.favorite_categories.map((cat, i) => (
                          <div key={cat.category} className="flex items-center gap-3">
                            <span className="w-6 text-center font-bold text-gray-500">#{i + 1}</span>
                            <div className="flex-1">
                              <div className="flex justify-between items-center mb-1">
                                <span className="font-medium capitalize">{cat.category}</span>
                                <span className="text-gray-400 text-sm">{cat.count} videos</span>
                              </div>
                              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-gradient-to-r from-red-600 to-orange-500 rounded-full transition-all duration-500"
                                  style={{ width: `${cat.percentage}%` }}
                                />
                              </div>
                            </div>
                            <span className="text-gray-400 text-sm w-12 text-right">{cat.percentage}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-[#141414] border border-white/10 rounded-xl p-5 text-center">
                      <Heart className="mx-auto text-gray-600 mb-2" size={24} />
                      <p className="text-gray-500 text-sm">Kijk meer videos om je favoriete categorieën te ontdekken</p>
                    </div>
                  )}

                  {/* Extra Stats Row - Clickable */}
                  <div className="grid grid-cols-2 gap-4">
                    <div 
                      className={`bg-[#141414] border rounded-xl p-4 text-center cursor-pointer transition-all hover:border-cyan-500/50 ${expandedStatCard === 'unique' ? 'border-cyan-500 ring-2 ring-cyan-500/20' : 'border-white/10'}`}
                      onClick={() => setExpandedStatCard(expandedStatCard === 'unique' ? null : 'unique')}
                      data-testid="stat-card-unique"
                    >
                      <div className="flex items-center justify-center gap-2">
                        <Eye className="text-cyan-400" size={24} />
                        <ChevronDown className={`text-gray-500 transition-transform ${expandedStatCard === 'unique' ? 'rotate-180' : ''}`} size={16} />
                      </div>
                      <p className="text-2xl font-bold text-white mt-2">{statistics.unique_videos || 0}</p>
                      <p className="text-gray-500 text-xs">Unieke videos</p>
                    </div>
                    <div className="bg-[#141414] border border-white/10 rounded-xl p-4 text-center">
                      <Flame className={`mx-auto mb-2 ${statistics.streak_days > 0 ? 'text-orange-500' : 'text-gray-600'}`} size={24} />
                      <p className="text-2xl font-bold text-white">{statistics.streak_days || 0}</p>
                      <p className="text-gray-500 text-xs">Dagen streak</p>
                    </div>
                  </div>

                  {/* Expanded Unique Videos List */}
                  {expandedStatCard === 'unique' && statistics.unique_videos_list && (
                    <div className="bg-[#141414] border border-white/10 rounded-xl p-4 animate-in slide-in-from-top-2 duration-200" data-testid="expanded-unique-videos">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-semibold text-white flex items-center gap-2">
                          <Eye className="text-cyan-400" size={18} /> Alle unieke videos ({statistics.unique_videos})
                        </h4>
                        <button 
                          onClick={() => setExpandedStatCard(null)}
                          className="text-gray-500 hover:text-white transition"
                        >
                          <X size={18} />
                        </button>
                      </div>
                      
                      {statistics.unique_videos_list.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <Eye className="mx-auto mb-2 opacity-50" size={32} />
                          <p>Nog geen videos bekeken</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-80 overflow-y-auto">
                          {statistics.unique_videos_list.map((video, idx) => (
                            <div 
                              key={`unique-${video.id}-${idx}`}
                              className="group cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (user?.is_premium) {
                                  setActiveVideo({ ...video, id: video.id });
                                } else {
                                  setShowPremiumModal(true);
                                }
                              }}
                            >
                              <div className="relative aspect-video rounded-lg overflow-hidden bg-black/50 mb-2">
                                {video.thumbnail ? (
                                  <img 
                                    src={video.thumbnail} 
                                    alt={video.title}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center bg-gray-800">
                                    <Play className="text-gray-600" size={24} />
                                  </div>
                                )}
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition flex items-center justify-center opacity-0 group-hover:opacity-100">
                                  <Play className="text-white" size={24} />
                                </div>
                              </div>
                              <p className="text-xs text-white font-medium line-clamp-2 group-hover:text-red-400 transition">{video.title}</p>
                              <p className="text-xs text-gray-500 truncate">{video.channel}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Member Since */}
                  {statistics.member_since && (
                    <div className="text-center text-gray-500 text-sm">
                      📅 Eerste video gekeken op {new Date(statistics.member_since).toLocaleDateString('nl-NL')}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-20">
                  <BarChart3 className="mx-auto text-gray-600 mb-4" size={48} />
                  <h3 className="text-xl font-semibold mb-2">Nog geen statistieken</h3>
                  <p className="text-gray-400">Begin met videos kijken om je statistieken te zien</p>
                </div>
              )}
            </div>
          )}

          {/* Notes Overview */}
          {showNotes && (
            <div className="mb-10">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-display text-2xl font-bold flex items-center gap-2">
                    <FileText className="text-blue-500" size={24} /> Mijn Notities
                  </h2>
                  <p className="text-gray-400 text-sm mt-1">{userNotes.length} notities opgeslagen</p>
                </div>
                <button onClick={() => setShowNotes(false)} className="text-gray-400 hover:text-white">
                  <X size={20} />
                </button>
              </div>
              
              {notesLoading ? (
                <div className="flex items-center justify-center py-20">
                  <RefreshCw className="animate-spin text-blue-500" size={32} />
                </div>
              ) : userNotes.length === 0 ? (
                <div className="text-center py-20">
                  <FileText className="mx-auto text-gray-600 mb-4" size={48} />
                  <h3 className="text-xl font-semibold mb-2">Nog geen notities</h3>
                  <p className="text-gray-400">Klik op het notitie icoon bij een video om een notitie toe te voegen</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {userNotes.map((note) => (
                    <div key={note.video_id} className="bg-[#141414] border border-white/10 rounded-xl p-4 hover:border-blue-500/30 transition">
                      <div className="flex gap-4">
                        {/* Video Thumbnail */}
                        <div className="flex-shrink-0 w-32 h-20 rounded-lg overflow-hidden bg-black">
                          <img 
                            src={note.video_thumbnail} 
                            alt={note.video_title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        
                        {/* Note Content */}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-white truncate mb-1">{note.video_title}</h4>
                          <p className="text-gray-500 text-xs mb-2">{note.video_channel}</p>
                          <p className="text-gray-300 text-sm line-clamp-2">{note.note_text}</p>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex-shrink-0 flex flex-col gap-2">
                          <a 
                            href={`https://www.youtube.com/watch?v=${note.video_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-red-600/20 hover:bg-red-600/40 text-red-500 rounded-lg transition"
                          >
                            <Play size={16} />
                          </a>
                          <button 
                            onClick={() => deleteNote(note.video_id)}
                            className="p-2 bg-white/10 hover:bg-red-600/30 text-gray-400 hover:text-red-500 rounded-lg transition"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                      
                      {/* Date */}
                      <p className="text-gray-600 text-xs mt-3">
                        Laatst bewerkt: {new Date(note.updated_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Saved Videos */}
          {showSavedVideos && (
            <div className="mb-10">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-display text-2xl font-bold flex items-center gap-2">
                    <Bookmark className="text-yellow-500" size={24} /> Opgeslagen Videos
                  </h2>
                  <p className="text-gray-400 text-sm mt-1">{savedVideos.length} van 50 videos opgeslagen</p>
                </div>
                <button onClick={() => setShowSavedVideos(false)} className="text-gray-400 hover:text-white">
                  <X size={20} />
                </button>
              </div>
              
              {savedVideos.length === 0 ? (
                <div className="text-center py-20">
                  <Bookmark className="mx-auto text-gray-600 mb-4" size={48} />
                  <h3 className="text-xl font-semibold mb-2">Nog geen opgeslagen videos</h3>
                  <p className="text-gray-400">Klik op het bookmark icoon bij een video om deze op te slaan</p>
                </div>
              ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
                {savedVideos.map((video, i) => (
                  <VideoCard key={video.video_id} video={{...video, id: video.video_id}} index={i} showRemove={true} />
                ))}
              </div>
              )}
            </div>
          )}

          {/* Category Videos */}
          {selectedCategory && !showSavedVideos && !showStatistics && !showNotes && (
            <div className="mb-10">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-display text-2xl font-bold">
                  {categories.find(c => c.id === selectedCategory)?.name}
                </h2>
                <span className="text-gray-400">{categoryVideos.length} video's</span>
              </div>
              
              {!user?.is_premium && (
                <div className="bg-gradient-to-r from-red-600/20 to-orange-500/10 border border-red-500/30 rounded-xl p-4 mb-6 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Crown className="text-yellow-500" size={20} />
                    <span className="text-white font-medium"><strong className="text-yellow-400">€4,99</strong>/maand — alle categorieën gepersonaliseerd voor jou!</span>
                  </div>
                  <button onClick={() => navigate("/premium")} className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-full text-sm font-semibold transition">
                    Upgrade Nu
                  </button>
                </div>
              )}
              
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
                {categoryVideos.map((video, i) => (
                  <VideoCard key={video.id} video={video} index={i} />
                ))}
              </div>
            </div>
          )}

          {/* Default Home View */}
          {!selectedCategory && !showSavedVideos && !showStatistics && !showNotes && (
            <>
              {/* Trending Section */}
              <section className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-display text-xl font-bold flex items-center gap-2">
                    {region === "us" ? "🇺🇸 Trending USA" : "🔥 Trending NL"}
                  </h2>
                  <button onClick={() => loadCategory("trending")} className="text-red-500 hover:text-red-400 text-sm">
                    Alles →
                  </button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
                  {trendingVideos.slice(0, 12).map((video, i) => (
                    <VideoCard key={video.id} video={video} index={i} />
                  ))}
                </div>
              </section>

              {/* Category Quick Access */}
              <section>
                <h2 className="font-display text-xl font-bold mb-4">📱 Categorieën</h2>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
                  {categories.slice(0, 16).map((cat, i) => {
                    const Icon = getCategoryIcon(cat.id);
                    return (
                      <button
                        key={cat.id}
                        onClick={() => loadCategory(cat.id)}
                        className="bg-[#141414] border border-white/5 rounded-lg p-3 text-center hover:border-red-600/50 hover:bg-red-600/5 transition group"
                        data-testid={`quick-cat-${cat.id}`}
                      >
                        <Icon className="mx-auto mb-1 text-gray-400 group-hover:text-red-500 transition" size={18} />
                        <span className="text-xs truncate block">{cat.name.replace(/[🔥🎤🇳🇱🎙️🎵⚽😂📰🎮📱📹💪🍳✈️👗🏠💻📚🔬💼💰🔍🎥😴🐾🚗🏆🎨👶]/g, "").trim()}</span>
                      </button>
                    );
                  })}
                </div>
              </section>
            </>
          )}
        </main>

      {/* Premium Modal */}
      {showPremiumModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={() => setShowPremiumModal(false)}>
          <div className="bg-[#141414] border border-white/10 rounded-2xl p-8 max-w-md w-full text-center" onClick={e => e.stopPropagation()}>
            <div className="w-20 h-20 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-6">
              <Lock className="text-yellow-500" size={36} />
            </div>
            <h3 className="font-display text-2xl font-bold mb-2">Premium Vereist</h3>
            <p className="text-gray-400 mb-6">
              Gratis gebruikers kunnen video's bekijken maar niet afspelen. Upgrade naar Premium voor onbeperkt kijken!
            </p>
            <div className="space-y-3">
              <button onClick={() => { setShowPremiumModal(false); navigate("/premium"); }} className="w-full py-3 bg-red-600 hover:bg-red-700 rounded-full font-semibold transition" data-testid="modal-upgrade-btn">
                Upgrade naar Premium - €4,99/maand
              </button>
              <button onClick={() => setShowPremiumModal(false)} className="w-full py-3 bg-white/10 hover:bg-white/20 rounded-full transition">
                Nee, ik blijf gratis
              </button>
            </div>
          </div>
        </div>
      )}

      {/* In-App Video Player Modal */}
      {activeVideo && (
        <div className="fixed inset-0 bg-black/95 z-50 flex flex-col" data-testid="video-player-modal">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <button 
                onClick={() => setActiveVideo(null)} 
                className="p-2 hover:bg-white/10 rounded-full transition"
                data-testid="close-video-player-btn"
              >
                <X size={24} />
              </button>
              <div className="min-w-0">
                <h2 className="font-semibold text-white truncate">{activeVideo.title}</h2>
                <p className="text-gray-400 text-sm truncate">{activeVideo.channel}</p>
              </div>
            </div>
          </div>
          
          {/* Video Player */}
          <div className="flex-1 flex items-center justify-center bg-black p-4">
            <div className="w-full max-w-5xl aspect-video">
              <iframe
                src={`https://www.youtube.com/embed/${activeVideo.id}?autoplay=1&rel=0&modestbranding=1&playsinline=1&origin=${window.location.origin}`}
                className="w-full h-full rounded-lg"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
                title={activeVideo.title}
                data-testid="video-player-iframe"
              />
            </div>
          </div>
          
          {/* Video Info & Actions */}
          <div className="p-4 border-t border-white/10 bg-[#0a0a0a]">
            <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <span className="text-gray-400 text-sm">{activeVideo.views || "0 weergaven"}</span>
                <span className="text-gray-500">•</span>
                <span className="text-gray-400 text-sm">{activeVideo.duration}</span>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveNoteVideo(activeVideo);
                    const existingNote = userNotes.find(n => n.video_id === activeVideo.id);
                    setNoteText(existingNote ? existingNote.note_text : "");
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-full transition text-sm"
                  data-testid="video-player-note-btn"
                >
                  <FileText size={16} />
                  Notitie
                </button>
                <button 
                  onClick={() => setActiveVideo(null)}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-full transition text-sm font-semibold"
                  data-testid="video-player-close-btn"
                >
                  Sluiten
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {showDeleteAccountModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={() => setShowDeleteAccountModal(false)}>
          <div className="bg-[#141414] border border-red-500/30 rounded-2xl p-8 max-w-md w-full text-center" onClick={e => e.stopPropagation()}>
            <div className="w-20 h-20 rounded-full bg-red-600/20 flex items-center justify-center mx-auto mb-6">
              <Trash2 className="text-red-500" size={36} />
            </div>
            <h3 className="font-display text-2xl font-bold mb-2 text-red-500">Account Verwijderen</h3>
            <p className="text-gray-400 mb-4">
              Weet je zeker dat je je account wilt verwijderen? Dit kan niet ongedaan worden gemaakt.
            </p>
            {user?.is_premium && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-4">
                <p className="text-yellow-500 text-sm">
                  <strong>Let op:</strong> Je Premium abonnement wordt per direct geannuleerd en je verliest toegang tot alle premium functies.
                </p>
              </div>
            )}
            <div className="space-y-3">
              <button 
                onClick={handleDeleteAccount}
                disabled={deletingAccount}
                className="w-full py-3 bg-red-600 hover:bg-red-700 disabled:bg-red-600/50 rounded-full font-semibold transition flex items-center justify-center gap-2" 
                data-testid="confirm-delete-account-btn"
              >
                {deletingAccount ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Verwijderen...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    Ja, verwijder mijn account
                  </>
                )}
              </button>
              <button 
                onClick={() => setShowDeleteAccountModal(false)} 
                className="w-full py-3 bg-white/10 hover:bg-white/20 rounded-full transition"
                data-testid="cancel-delete-account-btn"
              >
                Nee, behoud mijn account
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Note Modal */}
      {activeNoteVideo && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => { setActiveNoteVideo(null); setNoteText(""); }}>
          <div className="bg-[#141414] border border-white/10 rounded-2xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            {/* Video Preview */}
            <div className="flex gap-4 mb-4">
              <div className="flex-shrink-0 w-24 h-16 rounded-lg overflow-hidden bg-black">
                <img 
                  src={activeNoteVideo.thumbnail} 
                  alt={activeNoteVideo.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-white text-sm line-clamp-2">{activeNoteVideo.title}</h4>
                <p className="text-gray-500 text-xs mt-1">{activeNoteVideo.channel}</p>
              </div>
              <button onClick={() => { setActiveNoteVideo(null); setNoteText(""); }} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>
            
            {/* Note Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <FileText size={14} className="inline mr-1" /> Jouw Notitie
              </label>
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Schrijf hier je notitie over deze video..."
                className="w-full h-32 bg-black/50 border border-white/10 rounded-lg p-3 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none resize-none"
                data-testid="note-textarea"
              />
            </div>
            
            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => saveNote(activeNoteVideo)}
                disabled={savingNote || !noteText.trim()}
                className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-xl font-semibold transition flex items-center justify-center gap-2"
                data-testid="save-note-btn"
              >
                {savingNote ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />}
                {savingNote ? "Opslaan..." : "Notitie Opslaan"}
              </button>
              <button
                onClick={() => { setActiveNoteVideo(null); setNoteText(""); }}
                className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-xl transition"
              >
                Annuleren
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== PREMIUM PAGE ==============
const PremiumPage = () => {
  const { user, token, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleCheckout = async (packageId) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        package_id: packageId,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.location.href = res.data.url;
    } catch (e) {
      console.error('Checkout error:', e);
      alert("Er ging iets mis. Probeer het opnieuw.");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] py-10 px-6">
      <div className="max-w-6xl mx-auto">
        <button onClick={() => navigate("/home")} className="text-gray-400 hover:text-white mb-8 flex items-center gap-2">
          ← Terug naar SmartTube
        </button>

        <div className="text-center mb-12">
          <div className="inline-block bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-1 rounded-full text-sm font-bold mb-4">
            💎 ABONNEMENTEN
          </div>
          <h1 className="font-display text-4xl font-bold mb-4">
            Kies je SmartTube Plan
          </h1>
          <p className="text-gray-400 text-lg">
            Premium (50+ videos) of Platinum (100+ videos) per categorie
          </p>
        </div>

        {user?.is_premium ? (
          <div className="bg-[#141414] border border-green-500/50 rounded-2xl p-8 text-center">
            <Crown className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="font-display text-2xl font-bold mb-2">
              Je bent {user.subscription_tier === 'platinum' ? '💎 Platinum' : '⭐ Premium'}! 🎉
            </h2>
            <p className="text-gray-400 mb-2">
              Geldig tot: {user.premium_expires ? new Date(user.premium_expires).toLocaleDateString("nl-NL") : "Onbeperkt"}
            </p>
            <p className="text-sm text-gray-500 mb-4">
              {user.subscription_tier === 'platinum' ? '100+ videos per categorie' : '50+ videos per categorie'}
            </p>
            <button onClick={() => navigate("/home")} className="px-8 py-3 bg-red-600 hover:bg-red-700 rounded-full font-semibold transition">
              Start met Kijken
            </button>
            {user.subscription_tier !== 'platinum' && (
              <p className="text-sm text-purple-400 mt-4">
                Wil je upgraden naar Platinum? Stuur een bericht via Support in je account.
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
              {/* Premium Monthly */}
              <div className="bg-[#141414] border border-white/10 rounded-2xl p-6">
                <h3 className="font-display text-lg font-semibold mb-2">Premium Maand</h3>
                <p className="text-3xl font-bold mb-1">€4,99<span className="text-sm text-gray-400">/maand</span></p>
                <p className="text-gray-400 text-sm mb-4">Automatisch doorlopend</p>
                
                <ul className="space-y-2 mb-6">
                  {["🎬 Videos kijken", "50+ videos/categorie", "31 categorieën", "Videos opslaan"].map((item, i) => (
                    <li key={`pm-${i}`} className="flex items-center gap-2 text-gray-300 text-xs">
                      <Check size={14} className="text-green-500" /> {item}
                    </li>
                  ))}
                </ul>

                <button onClick={() => handleCheckout("monthly")} disabled={loading} className="w-full py-2.5 bg-red-600 hover:bg-red-700 rounded-full font-semibold text-sm transition" data-testid="checkout-monthly-btn">
                  {loading ? "..." : "Kies Premium"}
                </button>
              </div>

              {/* Premium Yearly */}
              <div className="bg-gradient-to-b from-yellow-600/20 to-[#141414] border border-yellow-500/50 rounded-2xl p-6 relative">
                <div className="absolute -top-3 right-4 bg-gradient-to-r from-yellow-500 to-amber-500 text-black px-2 py-0.5 rounded-full text-xs font-bold">
                  -33%
                </div>
                <h3 className="font-display text-lg font-semibold mb-2">Premium Jaar</h3>
                <p className="text-3xl font-bold mb-1">€39,99<span className="text-sm text-gray-400">/jaar</span></p>
                <p className="text-yellow-400 text-sm mb-4">= €3,33/maand</p>
                
                <ul className="space-y-2 mb-6">
                  {["Alles van Premium", "💰 Bespaar €19,89", "12 maanden", "Prioriteit support"].map((item, i) => (
                    <li key={`py-${i}`} className="flex items-center gap-2 text-gray-300 text-xs">
                      <Check size={14} className="text-yellow-500" /> {item}
                    </li>
                  ))}
                </ul>

                <button onClick={() => handleCheckout("yearly")} disabled={loading} className="w-full py-2.5 bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 rounded-full text-black font-semibold text-sm transition" data-testid="checkout-yearly-btn">
                  {loading ? "..." : "Kies Jaarlijks"}
                </button>
              </div>

              {/* Platinum Monthly */}
              <div className="bg-gradient-to-b from-purple-600/30 to-[#141414] border-2 border-purple-500 rounded-2xl p-6 relative transform lg:scale-105">
                <div className="absolute -top-3 right-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-2 py-0.5 rounded-full text-xs font-bold animate-pulse">
                  💎 PLATINUM
                </div>
                <h3 className="font-display text-lg font-semibold mb-2 text-purple-300">Platinum Maand</h3>
                <p className="text-3xl font-bold mb-1">€9,99<span className="text-sm text-gray-400">/maand</span></p>
                <p className="text-purple-400 text-sm mb-4">Dubbele content!</p>
                
                <ul className="space-y-2 mb-6">
                  {["💎 100+ videos/categorie", "Alles van Premium", "Dubbele keuze", "VIP support"].map((item, i) => (
                    <li key={`plm-${i}`} className="flex items-center gap-2 text-purple-200 text-xs">
                      <Check size={14} className="text-purple-400" /> {item}
                    </li>
                  ))}
                </ul>

                <button onClick={() => handleCheckout("platinum_monthly")} disabled={loading} className="w-full py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 rounded-full font-semibold text-sm transition" data-testid="checkout-platinum-monthly-btn">
                  {loading ? "..." : "Kies Platinum"}
                </button>
              </div>

              {/* Platinum Yearly */}
              <div className="bg-gradient-to-b from-purple-600/20 to-[#141414] border border-purple-500/50 rounded-2xl p-6 relative">
                <div className="absolute -top-3 right-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-2 py-0.5 rounded-full text-xs font-bold">
                  -33%
                </div>
                <h3 className="font-display text-lg font-semibold mb-2 text-purple-300">Platinum Jaar</h3>
                <p className="text-3xl font-bold mb-1">€79,99<span className="text-sm text-gray-400">/jaar</span></p>
                <p className="text-purple-400 text-sm mb-4">= €6,67/maand</p>
                
                <ul className="space-y-2 mb-6">
                  {["Alles van Platinum", "💰 Bespaar €39,89", "12 maanden", "Exclusieve features"].map((item, i) => (
                    <li key={`ply-${i}`} className="flex items-center gap-2 text-purple-200 text-xs">
                      <Check size={14} className="text-purple-400" /> {item}
                    </li>
                  ))}
                </ul>

                <button onClick={() => handleCheckout("platinum_yearly")} disabled={loading} className="w-full py-2.5 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 rounded-full font-semibold text-sm transition" data-testid="checkout-platinum-yearly-btn">
                  {loading ? "..." : "Kies Platinum Jaar"}
                </button>
              </div>
            </div>

            {/* Payment Methods */}
            <div className="text-center">
              <p className="text-gray-400 mb-4">Betaalmethodes</p>
              <div className="flex items-center justify-center gap-4 flex-wrap">
                {["iDEAL", "Bancontact", "Credit Card", "SEPA"].map(method => (
                  <span key={method} className="px-4 py-2 bg-[#1a1a1a] rounded-lg text-sm text-gray-400">{method}</span>
                ))}
              </div>
            </div>

            {/* Privacy Verklaring Personalisatie */}
            <div className="mt-10 bg-[#141414] border border-white/10 rounded-2xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <Shield className="text-blue-400" size={20} />
                </div>
                <div>
                  <h4 className="font-semibold mb-2">🔒 Privacy & Personalisatie</h4>
                  <p className="text-gray-400 text-sm leading-relaxed mb-3">
                    SmartTube personaliseert je video-aanbevelingen op basis van je <strong className="text-white">kijkgedrag binnen de app</strong>. 
                    Wij hebben <strong className="text-green-400">GEEN toegang</strong> tot je Google-account of YouTube-kijkgeschiedenis.
                  </p>
                  <ul className="text-sm text-gray-400 space-y-1">
                    <li className="flex items-center gap-2">
                      <Check size={14} className="text-green-500" />
                      <span>Personalisatie werkt alleen met videos die je <em>binnen SmartTube</em> bekijkt</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check size={14} className="text-green-500" />
                      <span>Geen koppeling met externe accounts vereist</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check size={14} className="text-green-500" />
                      <span>Je data blijft privé en wordt niet gedeeld met derden</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ============== PAYMENT SUCCESS ==============
const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const { token, refreshUser } = useAuth();
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) return;
    
    let isMounted = true;
    let currentAttempts = 0;
    
    const pollPaymentStatus = async () => {
      if (currentAttempts >= 10) { 
        if (isMounted) setStatus("timeout"); 
        return; 
      }
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!isMounted) return;
        
        if (res.data.payment_status === "paid") {
          setStatus("success");
          await refreshUser();
        } else if (res.data.status === "expired") {
          setStatus("expired");
        } else {
          currentAttempts++;
          setAttempts(currentAttempts);
          setTimeout(pollPaymentStatus, 2000);
        }
      } catch (e) {
        if (!isMounted) return;
        currentAttempts++;
        setAttempts(currentAttempts);
        setTimeout(pollPaymentStatus, 2000);
      }
    };
    
    pollPaymentStatus();
    
    return () => { isMounted = false; };
  }, [sessionId, token, refreshUser]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-6">
      <div className="bg-[#141414] border border-white/10 rounded-2xl p-10 max-w-md w-full text-center" data-testid="payment-success-page">
        {status === "checking" && (
          <>
            <div className="w-20 h-20 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
            <h2 className="font-display text-2xl font-bold mb-2">Betaling Verwerken...</h2>
            <p className="text-gray-400">Even geduld</p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <Check className="text-green-500" size={40} />
            </div>
            <h2 className="font-display text-2xl font-bold mb-2">Welkom bij Premium! 🎉</h2>
            <p className="text-gray-400 mb-6">Je kunt nu onbeperkt video's kijken</p>
            <button onClick={() => navigate("/home")} className="px-8 py-3 bg-red-600 hover:bg-red-700 rounded-full font-semibold transition" data-testid="go-home-btn">
              Start met Kijken
            </button>
          </>
        )}
        {(status === "expired" || status === "timeout") && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
              <X className="text-red-500" size={40} />
            </div>
            <h2 className="font-display text-2xl font-bold mb-2">
              {status === "expired" ? "Betaling Verlopen" : "Time-out"}
            </h2>
            <p className="text-gray-400 mb-6">Probeer het opnieuw</p>
            <button onClick={() => navigate("/premium")} className="px-8 py-3 bg-red-600 hover:bg-red-700 rounded-full font-semibold transition">
              Terug naar Premium
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// ============== ADMIN PAGE ==============
const AdminPage = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [notificationText, setNotificationText] = useState("");
  const [sendingNotification, setSendingNotification] = useState(false);
  const [supportTickets, setSupportTickets] = useState([]);
  const [supportStats, setSupportStats] = useState({ open: 0, in_progress: 0, resolved: 0, total: 0 });
  const [selectedAdminTicket, setSelectedAdminTicket] = useState(null);
  const [adminReplyText, setAdminReplyText] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, usersRes, transRes, catRes, analyticsRes, supportRes] = await Promise.all([
          axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/admin/transactions`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/categories`),
          axios.get(`${API}/admin/analytics`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/admin/support/tickets`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setStats(statsRes.data);
        setUsers(usersRes.data);
        setTransactions(transRes.data);
        setCategories(catRes.data);
        setAnalytics(analyticsRes.data);
        setSupportTickets(supportRes.data.tickets || []);
        setSupportStats(supportRes.data.stats || { open: 0, in_progress: 0, resolved: 0, total: 0 });
      } catch (e) {
        // Production: error logged silently
      }
      setLoading(false);
    };
    
    fetchData();
  }, [token]);

  const refetchData = async () => {
    try {
      const [statsRes, usersRes, transRes, catRes, analyticsRes, supportRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/transactions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/categories`),
        axios.get(`${API}/admin/analytics`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/support/tickets`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setTransactions(transRes.data);
      setCategories(catRes.data);
      setAnalytics(analyticsRes.data);
      setSupportTickets(supportRes.data.tickets || []);
      setSupportStats(supportRes.data.stats || { open: 0, in_progress: 0, resolved: 0, total: 0 });
    } catch (e) {
      // Production: error logged silently
    }
  };

  const togglePremium = async (userId) => {
    try {
      await axios.put(`${API}/admin/user/${userId}/premium`, {}, { headers: { Authorization: `Bearer ${token}` } });
      refetchData();
    } catch (e) {
      // Production: error logged silently
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Weet je zeker dat je deze gebruiker wilt verwijderen?")) return;
    try {
      await axios.delete(`${API}/admin/user/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      refetchData();
    } catch (e) {
      // Production: error logged silently
    }
  };

  const sendNotification = async () => {
    if (!notificationText.trim()) return;
    setSendingNotification(true);
    try {
      const res = await axios.post(`${API}/admin/notification`, 
        { message: notificationText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`✅ ${res.data.message}`);
      setNotificationText("");
    } catch (e) {
      alert("❌ Kon notificatie niet versturen");
    }
    setSendingNotification(false);
  };

  if (loading) return <LoadingScreen />;

  const premiumUsers = users.filter(u => u.is_premium);
  const freeUsers = users.filter(u => !u.is_premium);
  const paidTransactions = transactions.filter(t => t.payment_status === "paid");
  const conversionRate = users.length > 0 ? ((premiumUsers.length / users.length) * 100).toFixed(1) : 0;

  return (
    <div className="min-h-screen bg-[#0a0a0a] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <button onClick={() => navigate("/home")} className="text-gray-400 hover:text-white mb-2 flex items-center gap-2">← Terug naar SmartTube</button>
            <h1 className="font-display text-3xl font-bold">Owner Dashboard</h1>
            <p className="text-gray-400 mt-1">Beheer alles van SmartTube</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="bg-cyan-500/20 text-cyan-400 px-3 py-1 rounded-full text-sm font-semibold">👑 Owner</span>
            <span className="text-gray-400 text-sm">{user?.email}</span>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-8">
          {[
            { label: "Totaal Gebruikers", value: stats?.total_users || 0, icon: Users, color: "text-blue-500" },
            { label: "Premium Users", value: stats?.premium_users || 0, icon: Crown, color: "text-yellow-500" },
            { label: "Gratis Users", value: freeUsers.length, icon: User, color: "text-gray-500" },
            { label: "Totale Omzet", value: `€${(stats?.total_revenue || 0).toFixed(2)}`, icon: TrendingUp, color: "text-green-500" },
            { label: "Conversie", value: `${conversionRate}%`, icon: Zap, color: "text-orange-500" }
          ].map((stat, i) => (
            <div key={i} className="bg-[#141414] border border-white/5 rounded-xl p-4">
              <stat.icon className={`${stat.color} mb-2`} size={20} />
              <p className="text-2xl font-bold">{stat.value}</p>
              <p className="text-gray-400 text-xs">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Usage Analytics */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="bg-gradient-to-br from-purple-600/20 to-purple-900/10 border border-purple-500/30 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <Clock className="text-purple-400" size={20} />
              </div>
              <div>
                <p className="text-gray-400 text-xs">Totale Gebruikstijd</p>
                <p className="text-2xl font-bold text-purple-400">{stats?.weekly_usage_hours || 0} uur</p>
              </div>
            </div>
            <p className="text-gray-500 text-xs">Deze week</p>
          </div>
          
          <div className="bg-gradient-to-br from-cyan-600/20 to-cyan-900/10 border border-cyan-500/30 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                <TrendingUp className="text-cyan-400" size={20} />
              </div>
              <div>
                <p className="text-gray-400 text-xs">Gem. Sessieduur</p>
                <p className="text-2xl font-bold text-cyan-400">{stats?.avg_session_minutes || 0} min</p>
              </div>
            </div>
            <p className="text-gray-500 text-xs">Per gebruiker</p>
          </div>
          
          <div className="bg-gradient-to-br from-green-600/20 to-green-900/10 border border-green-500/30 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <Users className="text-green-400" size={20} />
              </div>
              <div>
                <p className="text-gray-400 text-xs">Actieve Gebruikers</p>
                <p className="text-2xl font-bold text-green-400">{stats?.active_users_this_week || 0}</p>
              </div>
            </div>
            <p className="text-gray-500 text-xs">Deze week</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            { id: "overview", label: "📊 Overzicht" },
            { id: "analytics", label: "📈 Analytics" },
            { id: "users", label: "👥 Gebruikers" },
            { id: "transactions", label: "💳 Transacties" },
            { id: "categories", label: "📁 Categorieën" },
            { id: "notifications", label: "🔔 Notificaties" },
            { id: "support", label: "💬 Support" },
            { id: "settings", label: "⚙️ Instellingen" }
          ].map(tab => (
            <button 
              key={tab.id} 
              onClick={() => setActiveTab(tab.id)} 
              className={`px-4 py-2 rounded-lg transition text-sm ${activeTab === tab.id ? "bg-red-600 text-white" : "bg-[#1a1a1a] text-gray-400 hover:text-white"}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-[#141414] border border-white/5 rounded-xl p-6">
          
          {/* OVERVIEW TAB */}
          {activeTab === "overview" && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Recent Transactions */}
                <div>
                  <h3 className="font-semibold mb-4 flex items-center gap-2"><CreditCard size={18} /> Recente Betalingen</h3>
                  <div className="space-y-2">
                    {stats?.recent_transactions?.slice(0, 5).map((t, i) => (
                      <div key={i} className="flex items-center justify-between py-2 px-3 bg-[#1a1a1a] rounded-lg">
                        <span className="text-sm truncate max-w-[150px]">{t.user_email}</span>
                        <span className="text-gray-400 text-sm">€{t.amount}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${t.payment_status === "paid" ? "bg-green-500/20 text-green-500" : "bg-yellow-500/20 text-yellow-500"}`}>
                          {t.payment_status}
                        </span>
                      </div>
                    ))}
                    {(!stats?.recent_transactions || stats.recent_transactions.length === 0) && (
                      <p className="text-gray-500 text-sm">Nog geen transacties</p>
                    )}
                  </div>
                </div>

                {/* Quick Stats */}
                <div>
                  <h3 className="font-semibold mb-4 flex items-center gap-2"><TrendingUp size={18} /> Platform Stats</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 px-3 bg-[#1a1a1a] rounded-lg">
                      <span className="text-gray-400 text-sm">Categorieën</span>
                      <span className="font-semibold">{categories.length}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 px-3 bg-[#1a1a1a] rounded-lg">
                      <span className="text-gray-400 text-sm">Videos per categorie</span>
                      <span className="font-semibold">50</span>
                    </div>
                    <div className="flex justify-between items-center py-2 px-3 bg-[#1a1a1a] rounded-lg">
                      <span className="text-gray-400 text-sm">Refresh interval</span>
                      <span className="font-semibold">72 uur</span>
                    </div>
                    <div className="flex justify-between items-center py-2 px-3 bg-[#1a1a1a] rounded-lg">
                      <span className="text-gray-400 text-sm">Premium prijs</span>
                      <span className="font-semibold text-green-500">€4,99/maand</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ANALYTICS TAB */}
          {activeTab === "analytics" && (
            <div className="space-y-6">
              <h3 className="font-semibold text-lg flex items-center gap-2">📈 Gedetailleerde Analytics</h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Top Categorieën */}
                <div className="bg-[#1a1a1a] rounded-xl p-5">
                  <h4 className="font-medium mb-4 flex items-center gap-2"><Grid size={16} /> Populairste Categorieën</h4>
                  <div className="space-y-2">
                    {analytics?.category_stats?.slice(0, 8).map((cat, i) => (
                      <div key={i} className="flex items-center justify-between py-2 px-3 bg-[#141414] rounded-lg">
                        <span className="text-sm">{cat.category}</span>
                        <span className="text-gray-400 text-sm">{cat.videos} videos</span>
                      </div>
                    ))}
                    {(!analytics?.category_stats || analytics.category_stats.length === 0) && (
                      <p className="text-gray-500 text-sm">Nog geen data beschikbaar</p>
                    )}
                  </div>
                </div>

                {/* Omzet per Maand */}
                <div className="bg-[#1a1a1a] rounded-xl p-5">
                  <h4 className="font-medium mb-4 flex items-center gap-2"><TrendingUp size={16} /> Omzet per Maand</h4>
                  <div className="space-y-2">
                    {analytics?.revenue_by_month?.map((rev, i) => (
                      <div key={i} className="flex items-center justify-between py-2 px-3 bg-[#141414] rounded-lg">
                        <span className="text-sm">{rev.month}</span>
                        <span className="text-green-500 font-semibold">€{rev.revenue.toFixed(2)}</span>
                      </div>
                    ))}
                    {(!analytics?.revenue_by_month || analytics.revenue_by_month.length === 0) && (
                      <p className="text-gray-500 text-sm">Nog geen omzet data</p>
                    )}
                  </div>
                </div>

                {/* Gebruikersgroei */}
                <div className="bg-[#1a1a1a] rounded-xl p-5">
                  <h4 className="font-medium mb-4 flex items-center gap-2"><Users size={16} /> Gebruikersgroei per Maand</h4>
                  <div className="space-y-2">
                    {analytics?.user_growth?.map((growth, i) => (
                      <div key={i} className="flex items-center justify-between py-2 px-3 bg-[#141414] rounded-lg">
                        <span className="text-sm">{growth.period}</span>
                        <span className="text-blue-400 font-semibold">+{growth.new_users} users</span>
                      </div>
                    ))}
                    {(!analytics?.user_growth || analytics.user_growth.length === 0) && (
                      <p className="text-gray-500 text-sm">Nog geen groei data</p>
                    )}
                  </div>
                </div>

                {/* Top Opgeslagen Videos */}
                <div className="bg-[#1a1a1a] rounded-xl p-5">
                  <h4 className="font-medium mb-4 flex items-center gap-2"><Bookmark size={16} /> Meest Opgeslagen Videos</h4>
                  <div className="space-y-2">
                    {analytics?.top_saved_videos?.map((video, i) => (
                      <div key={i} className="flex items-center justify-between py-2 px-3 bg-[#141414] rounded-lg">
                        <span className="text-sm truncate max-w-[200px]">{video._id}</span>
                        <span className="text-yellow-500 font-semibold">{video.count}x opgeslagen</span>
                      </div>
                    ))}
                    {(!analytics?.top_saved_videos || analytics.top_saved_videos.length === 0) && (
                      <p className="text-gray-500 text-sm">Nog geen opgeslagen videos</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* USERS TAB */}
          {activeTab === "users" && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold">Alle Gebruikers ({users.length})</h3>
                <div className="flex gap-2 text-sm">
                  <span className="px-3 py-1 bg-yellow-500/20 text-yellow-500 rounded-full">{premiumUsers.length} Premium</span>
                  <span className="px-3 py-1 bg-gray-500/20 text-gray-400 rounded-full">{freeUsers.length} Gratis</span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-gray-400 border-b border-white/10 text-sm">
                      <th className="pb-3">Naam</th>
                      <th className="pb-3">Email</th>
                      <th className="pb-3">Status</th>
                      <th className="pb-3">Aangemeld</th>
                      <th className="pb-3">Acties</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.id} className="border-b border-white/5 text-sm">
                        <td className="py-3">{u.name}</td>
                        <td className="py-3 text-gray-400">{u.email}</td>
                        <td className="py-3">
                          <div className="flex gap-1">
                            {u.is_premium && <span className="text-xs bg-yellow-500 text-black px-2 py-0.5 rounded-full">Premium</span>}
                            {u.is_owner && <span className="text-xs bg-cyan-500/20 text-cyan-400 px-2 py-0.5 rounded-full">Owner</span>}
                            {!u.is_premium && !u.is_owner && <span className="text-xs bg-gray-500/20 text-gray-400 px-2 py-0.5 rounded-full">Gratis</span>}
                          </div>
                        </td>
                        <td className="py-3 text-gray-400">{new Date(u.created_at).toLocaleDateString("nl-NL")}</td>
                        <td className="py-3">
                          {!u.is_owner && (
                            <div className="flex gap-1">
                              <button onClick={() => togglePremium(u.id)} className="p-1.5 bg-[#1a1a1a] hover:bg-white/10 rounded-lg" title={u.is_premium ? "Verwijder Premium" : "Geef Premium"}>
                                <Crown size={14} className={u.is_premium ? "text-yellow-500" : "text-gray-500"} />
                              </button>
                              <button onClick={() => deleteUser(u.id)} className="p-1.5 bg-[#1a1a1a] hover:bg-red-500/20 rounded-lg" title="Verwijder">
                                <Trash2 size={14} className="text-red-500" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TRANSACTIONS TAB */}
          {activeTab === "transactions" && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold">Transacties ({transactions.length})</h3>
                <span className="text-green-500 font-semibold">Totaal: €{(stats?.total_revenue || 0).toFixed(2)}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-gray-400 border-b border-white/10 text-sm">
                      <th className="pb-3">Email</th>
                      <th className="pb-3">Pakket</th>
                      <th className="pb-3">Bedrag</th>
                      <th className="pb-3">Status</th>
                      <th className="pb-3">Datum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map(t => (
                      <tr key={t.id} className="border-b border-white/5 text-sm">
                        <td className="py-3">{t.user_email}</td>
                        <td className="py-3 capitalize">{t.package_id}</td>
                        <td className="py-3 font-semibold">€{t.amount}</td>
                        <td className="py-3">
                          <span className={`px-2 py-1 rounded-full text-xs ${t.payment_status === "paid" ? "bg-green-500/20 text-green-500" : t.payment_status === "initiated" ? "bg-yellow-500/20 text-yellow-500" : "bg-red-500/20 text-red-500"}`}>
                            {t.payment_status === "paid" ? "Betaald" : t.payment_status === "initiated" ? "Gestart" : t.payment_status}
                          </span>
                        </td>
                        <td className="py-3 text-gray-400">{new Date(t.created_at).toLocaleDateString("nl-NL")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {transactions.length === 0 && <p className="text-gray-500 text-center py-8">Nog geen transacties</p>}
              </div>
            </div>
          )}

          {/* CATEGORIES TAB */}
          {activeTab === "categories" && (
            <div>
              <h3 className="font-semibold mb-4">Categorieën Beheer ({categories.length})</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {categories.map((cat, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-[#1a1a1a] rounded-lg">
                    <div>
                      <p className="font-medium text-sm">{cat.name}</p>
                      <p className="text-gray-500 text-xs">{cat.description}</p>
                    </div>
                    <span className="text-xs text-gray-400 bg-[#0a0a0a] px-2 py-1 rounded">50 videos</span>
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-sm mt-4">* Categorieën worden automatisch bijgewerkt. Videos refreshen elke 72 uur voor premium gebruikers.</p>
            </div>
          )}

          {/* NOTIFICATIONS TAB */}
          {activeTab === "notifications" && (
            <div>
              <h3 className="font-semibold mb-4 flex items-center gap-2"><Bell size={18} /> Push Notificaties</h3>
              <div className="max-w-xl">
                <p className="text-gray-400 text-sm mb-4">Stuur een push notificatie naar alle premium gebruikers</p>
                <div className="space-y-4">
                  <textarea
                    value={notificationText}
                    onChange={(e) => setNotificationText(e.target.value)}
                    placeholder="Typ je notificatie bericht..."
                    className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-gray-600 focus:border-red-600 focus:outline-none transition min-h-[100px]"
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 text-sm">Ontvangers: {premiumUsers.length} premium gebruikers</span>
                    <button 
                      onClick={sendNotification} 
                      disabled={sendingNotification || !notificationText.trim()}
                      className="px-6 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg font-semibold transition flex items-center gap-2"
                    >
                      <Bell size={16} />
                      {sendingNotification ? "Versturen..." : "Verstuur Notificatie"}
                    </button>
                  </div>
                </div>
                
                <div className="mt-8 p-4 bg-[#1a1a1a] rounded-lg">
                  <h4 className="font-semibold mb-2">📱 Automatische Notificaties</h4>
                  <ul className="space-y-2 text-sm text-gray-400">
                    <li className="flex items-center gap-2"><Check size={14} className="text-green-500" /> Nieuwe video's in favoriete categorieën</li>
                    <li className="flex items-center gap-2"><Check size={14} className="text-green-500" /> 72-uur content refresh melding</li>
                    <li className="flex items-center gap-2"><Check size={14} className="text-green-500" /> Premium verloopdatum herinnering</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* SUPPORT TAB */}
          {activeTab === "support" && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold flex items-center gap-2"><MessageSquare size={18} /> Support Tickets</h3>
                <button onClick={refetchData} className="text-sm text-gray-400 hover:text-white flex items-center gap-1">
                  <RefreshCw size={14} /> Vernieuwen
                </button>
              </div>
              
              {/* Support Stats */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-yellow-400">{supportStats.open}</p>
                  <p className="text-xs text-gray-400">Open</p>
                </div>
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-blue-400">{supportStats.in_progress}</p>
                  <p className="text-xs text-gray-400">In Behandeling</p>
                </div>
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-400">{supportStats.resolved}</p>
                  <p className="text-xs text-gray-400">Opgelost</p>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold">{supportStats.total}</p>
                  <p className="text-xs text-gray-400">Totaal</p>
                </div>
              </div>

              {/* Tickets List */}
              {supportTickets.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <MessageSquare className="mx-auto mb-2" size={32} />
                  <p>Geen support tickets</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {supportTickets.map(ticket => (
                    <div 
                      key={ticket.id}
                      onClick={() => setSelectedAdminTicket(ticket)}
                      className="bg-[#1a1a1a] border border-white/10 rounded-lg p-4 hover:border-red-600/50 cursor-pointer transition"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              ticket.status === 'open' ? 'bg-yellow-500/20 text-yellow-400' :
                              ticket.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400' :
                              ticket.status === 'resolved' ? 'bg-green-500/20 text-green-400' :
                              'bg-gray-500/20 text-gray-400'
                            }`}>
                              {ticket.status === 'open' ? 'Open' : ticket.status === 'in_progress' ? 'In Behandeling' : ticket.status === 'resolved' ? 'Opgelost' : 'Gesloten'}
                            </span>
                            <span className="text-xs text-gray-500">{ticket.category}</span>
                          </div>
                          <h4 className="font-semibold">{ticket.subject}</h4>
                          <p className="text-sm text-gray-400">{ticket.user_email}</p>
                        </div>
                        <div className="text-right text-sm">
                          <p className="text-gray-500">{new Date(ticket.created_at).toLocaleDateString('nl-NL')}</p>
                          {ticket.replies && ticket.replies.length > 0 && (
                            <p className="text-xs text-gray-600">{ticket.replies.length} reactie(s)</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Ticket Detail Modal */}
              {selectedAdminTicket && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                  <div className="bg-[#141414] rounded-2xl p-6 max-w-2xl w-full border border-white/10 max-h-[80vh] overflow-y-auto">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            selectedAdminTicket.status === 'open' ? 'bg-yellow-500/20 text-yellow-400' :
                            selectedAdminTicket.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400' :
                            selectedAdminTicket.status === 'resolved' ? 'bg-green-500/20 text-green-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                            {selectedAdminTicket.status === 'open' ? 'Open' : selectedAdminTicket.status === 'in_progress' ? 'In Behandeling' : selectedAdminTicket.status === 'resolved' ? 'Opgelost' : 'Gesloten'}
                          </span>
                          <select 
                            value={selectedAdminTicket.status}
                            onChange={async (e) => {
                              try {
                                await axios.put(`${API}/admin/support/tickets/${selectedAdminTicket.id}/status?status=${e.target.value}`, {}, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                refetchData();
                                setSelectedAdminTicket({...selectedAdminTicket, status: e.target.value});
                              } catch (err) {
                                console.error('Error updating status:', err);
                              }
                            }}
                            className="text-xs bg-white/10 border border-white/20 rounded px-2 py-1"
                          >
                            <option value="open">Open</option>
                            <option value="in_progress">In Behandeling</option>
                            <option value="resolved">Opgelost</option>
                            <option value="closed">Gesloten</option>
                          </select>
                        </div>
                        <h2 className="text-xl font-bold">{selectedAdminTicket.subject}</h2>
                        <p className="text-sm text-gray-400">
                          Van: {selectedAdminTicket.user_email} ({selectedAdminTicket.user_name}) • {new Date(selectedAdminTicket.created_at).toLocaleDateString('nl-NL')}
                        </p>
                      </div>
                      <button onClick={() => setSelectedAdminTicket(null)} className="text-gray-400 hover:text-white">
                        <X size={24} />
                      </button>
                    </div>
                    
                    {/* Original Message */}
                    <div className="bg-white/5 rounded-lg p-4 mb-4">
                      <p className="text-sm text-gray-400 mb-1">Origineel bericht:</p>
                      <p className="text-white whitespace-pre-wrap">{selectedAdminTicket.message}</p>
                    </div>
                    
                    {/* Replies */}
                    {selectedAdminTicket.replies && selectedAdminTicket.replies.length > 0 && (
                      <div className="space-y-3 mb-4">
                        {selectedAdminTicket.replies.map((reply, idx) => (
                          <div key={idx} className={`p-4 rounded-lg ${reply.from_admin ? 'bg-red-600/10 border border-red-600/30' : 'bg-white/5'}`}>
                            <p className="text-sm text-gray-400 mb-1">
                              {reply.from_admin ? `${reply.admin_name} (Admin)` : selectedAdminTicket.user_name} • {new Date(reply.created_at).toLocaleDateString('nl-NL')}
                            </p>
                            <p className="text-white whitespace-pre-wrap">{reply.message}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Admin Reply Form */}
                    <div className="border-t border-white/10 pt-4">
                      <textarea 
                        value={adminReplyText}
                        onChange={(e) => setAdminReplyText(e.target.value)}
                        placeholder="Schrijf een antwoord..."
                        rows={3}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 focus:border-red-500 outline-none resize-none mb-3"
                      />
                      <button 
                        onClick={async () => {
                          if (!adminReplyText.trim()) return;
                          try {
                            await axios.post(`${API}/admin/support/tickets/${selectedAdminTicket.id}/reply`, 
                              { message: adminReplyText },
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            setAdminReplyText('');
                            refetchData();
                            // Refresh ticket data
                            const res = await axios.get(`${API}/admin/support/tickets`, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            const updatedTicket = res.data.tickets.find(t => t.id === selectedAdminTicket.id);
                            if (updatedTicket) setSelectedAdminTicket(updatedTicket);
                          } catch (err) {
                            console.error('Error sending reply:', err);
                          }
                        }}
                        disabled={!adminReplyText.trim()}
                        className="px-6 py-2 bg-red-600 hover:bg-red-700 rounded-full font-medium transition disabled:opacity-50"
                      >
                        Verstuur Antwoord
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* SETTINGS TAB */}
          {activeTab === "settings" && (
            <div>
              <h3 className="font-semibold mb-4 flex items-center gap-2"><Settings size={18} /> Platform Instellingen</h3>
              <div className="space-y-6 max-w-xl">
                {/* Pricing */}
                <div className="p-4 bg-[#1a1a1a] rounded-lg">
                  <h4 className="font-semibold mb-3">💰 Prijzen</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Maandelijks Premium</span>
                      <span className="font-semibold text-green-500">€4,99</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Jaarlijks Premium</span>
                      <span className="font-semibold text-green-500">€59,99</span>
                    </div>
                  </div>
                </div>

                {/* Content Settings */}
                <div className="p-4 bg-[#1a1a1a] rounded-lg">
                  <h4 className="font-semibold mb-3">🎬 Content Instellingen</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Videos per categorie</span>
                      <span className="font-semibold">50</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Personalisatie refresh</span>
                      <span className="font-semibold">72 uur</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Gratis users</span>
                      <span className="text-gray-400">Alleen scrollen</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Premium users</span>
                      <span className="text-green-500">Videos kijken + Gepersonaliseerd</span>
                    </div>
                  </div>
                </div>

                {/* Payment Methods */}
                <div className="p-4 bg-[#1a1a1a] rounded-lg">
                  <h4 className="font-semibold mb-3">💳 Betaalmethodes (Stripe)</h4>
                  <div className="flex flex-wrap gap-2">
                    {["iDEAL", "Bancontact", "Credit Card", "SEPA"].map(m => (
                      <span key={m} className="px-3 py-1 bg-green-500/20 text-green-500 rounded-full text-sm flex items-center gap-1">
                        <Check size={12} /> {m}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Owner Info */}
                <div className="p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                  <h4 className="font-semibold mb-2 text-cyan-400">👑 Owner Account</h4>
                  <p className="text-sm text-gray-400">Email: {user?.email}</p>
                  <p className="text-sm text-gray-400">Status: Premium (Onbeperkt)</p>
                  <p className="text-sm text-gray-400">Rechten: Volledige toegang</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============== PRIVACY POLICY PAGE ==============
const PrivacyPolicyPage = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <header className="border-b border-white/10 py-4">
        <div className="max-w-4xl mx-auto px-6 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="text-2xl font-bold">
            <span className="text-red-600">Smart</span>Tube
          </button>
          <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-white">← Terug</button>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold mb-8">Privacy Policy</h1>
        <div className="prose prose-invert max-w-none space-y-6 text-gray-300">
          <p><strong>Laatst bijgewerkt:</strong> 29 juli 2025</p>
          <p><strong>Effectief vanaf:</strong> 29 juli 2025</p>
          
          <h2 className="text-xl font-semibold text-white mt-8">1. Inleiding</h2>
          <p>SmartTube ("wij", "ons", "onze") respecteert uw privacy en zet zich in voor de bescherming van uw persoonlijke gegevens. Deze privacy policy informeert u over hoe wij omgaan met uw persoonlijke gegevens wanneer u onze website (smarttube.online) en mobiele applicatie ("App") gebruikt.</p>
          <p>SmartTube is een video discovery en curatie platform dat gebruikers helpt bij het ontdekken van YouTube content via de officiële YouTube IFrame Player API. U kunt contact met ons opnemen via het support systeem in de app.</p>
          
          <h2 className="text-xl font-semibold text-white mt-8">2. Gegevens die wij verzamelen</h2>
          <p>Wij verzamelen en verwerken de volgende categorieën persoonlijke gegevens:</p>
          
          <h3 className="text-lg font-semibold text-white mt-4">2.1 Account gegevens</h3>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Naam:</strong> Om u persoonlijk aan te spreken</li>
            <li><strong>E-mailadres:</strong> Voor accountverificatie en communicatie</li>
            <li><strong>Wachtwoord:</strong> Versleuteld opgeslagen (bcrypt hashing)</li>
          </ul>
          
          <h3 className="text-lg font-semibold text-white mt-4">2.2 Betalingsgegevens</h3>
          <ul className="list-disc pl-6 space-y-2">
            <li>Betalingen worden verwerkt door <strong>Stripe, Inc.</strong></li>
            <li>Wij slaan GEEN creditcard- of bankrekeningnummers op</li>
            <li>Stripe ontvangt uw betalingsgegevens rechtstreeks</li>
            <li>Wij ontvangen alleen een bevestiging van succesvolle betalingen</li>
          </ul>
          
          <h3 className="text-lg font-semibold text-white mt-4">2.3 Gebruiksgegevens</h3>
          <ul className="list-disc pl-6 space-y-2">
            <li>Bekeken video categorieën</li>
            <li>Opgeslagen favoriete video's</li>
            <li>Voorkeuren en personalisatie-instellingen</li>
            <li>Kijkgeschiedenis binnen de app</li>
          </ul>
          
          <h3 className="text-lg font-semibold text-white mt-4">2.4 Technische gegevens</h3>
          <ul className="list-disc pl-6 space-y-2">
            <li>IP-adres (geanonimiseerd voor analytics)</li>
            <li>Apparaattype en besturingssysteem</li>
            <li>Browsertype en -versie</li>
            <li>Tijdzone en taalinstellingen</li>
          </ul>
          
          <h2 className="text-xl font-semibold text-white mt-8">3. YouTube Content Integratie</h2>
          <p><strong>Belangrijk:</strong> SmartTube maakt gebruik van de officiële <strong>YouTube IFrame Player API</strong> om video's weer te geven. Dit betekent:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Video's worden rechtstreeks van YouTube servers geladen</li>
            <li>YouTube kan advertenties tonen binnen embedded video's conform hun advertentiebeleid</li>
            <li>YouTube's eigen privacybeleid is van toepassing op de videospeler</li>
            <li>Wij hebben geen toegang tot of controle over YouTube's advertentie-algoritmes</li>
            <li>Wij wijzigen, downloaden of herhosten geen YouTube content</li>
          </ul>
          <p>Door SmartTube te gebruiken, gaat u ook akkoord met <a href="https://www.youtube.com/t/terms" className="text-red-500" target="_blank" rel="noopener noreferrer">YouTube's Servicevoorwaarden</a> en <a href="https://policies.google.com/privacy" className="text-red-500" target="_blank" rel="noopener noreferrer">Google's Privacybeleid</a>.</p>
          
          <h2 className="text-xl font-semibold text-white mt-8">4. Doeleinden van gegevensverwerking</h2>
          <p>Wij gebruiken uw gegevens voor:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Dienstverlening:</strong> Het aanmaken en beheren van uw account</li>
            <li><strong>Personalisatie:</strong> Het tonen van relevante video categorieën op basis van uw voorkeuren</li>
            <li><strong>Betalingsverwerking:</strong> Het verwerken van Premium abonnementen via Stripe</li>
            <li><strong>Communicatie:</strong> Het versturen van accountgerelateerde e-mails (verificatie, wachtwoord reset)</li>
            <li><strong>Verbetering:</strong> Het analyseren van gebruikspatronen om onze diensten te verbeteren</li>
            <li><strong>Beveiliging:</strong> Het beschermen tegen fraude en misbruik</li>
          </ul>
          
          <h2 className="text-xl font-semibold text-white mt-8">5. Rechtsgronden voor verwerking (AVG/GDPR)</h2>
          <p>Wij verwerken uw gegevens op basis van:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Uitvoering van overeenkomst:</strong> Om onze diensten aan u te leveren</li>
            <li><strong>Toestemming:</strong> Wanneer u expliciet toestemming geeft</li>
            <li><strong>Gerechtvaardigd belang:</strong> Voor beveiliging en verbetering van diensten</li>
            <li><strong>Wettelijke verplichting:</strong> Wanneer wij wettelijk verplicht zijn gegevens te bewaren</li>
          </ul>
          
          <h2 className="text-xl font-semibold text-white mt-8">6. Delen van gegevens met derden</h2>
          <p>Wij delen uw gegevens uitsluitend met de volgende vertrouwde partijen:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Stripe, Inc.</strong> (VS) - Betalingsverwerking - <a href="https://stripe.com/privacy" className="text-red-500" target="_blank" rel="noopener noreferrer">Privacy Policy</a></li>
            <li><strong>Resend</strong> - E-mail verzending - <a href="https://resend.com/legal/privacy-policy" className="text-red-500" target="_blank" rel="noopener noreferrer">Privacy Policy</a></li>
            <li><strong>YouTube/Google</strong> - Video content via IFrame API - <a href="https://policies.google.com/privacy" className="text-red-500" target="_blank" rel="noopener noreferrer">Privacy Policy</a></li>
            <li><strong>MongoDB Atlas</strong> - Database hosting - <a href="https://www.mongodb.com/legal/privacy-policy" className="text-red-500" target="_blank" rel="noopener noreferrer">Privacy Policy</a></li>
          </ul>
          <p><strong>Wij verkopen NOOIT uw persoonlijke gegevens aan derden.</strong></p>
          
          <h2 className="text-xl font-semibold text-white mt-8">7. Internationale gegevensoverdracht</h2>
          <p>Sommige van onze dienstverleners zijn gevestigd buiten de Europese Economische Ruimte (EER). Wij zorgen ervoor dat passende waarborgen aanwezig zijn, zoals:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>EU-VS Data Privacy Framework certificering</li>
            <li>Standaard contractuele clausules (SCC's)</li>
          </ul>
          
          <h2 className="text-xl font-semibold text-white mt-8">8. Bewaartermijnen</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Accountgegevens:</strong> Zolang uw account actief is</li>
            <li><strong>Na accountverwijdering:</strong> Binnen 30 dagen volledig verwijderd</li>
            <li><strong>Betalingsgeschiedenis:</strong> 7 jaar (wettelijke verplichting)</li>
            <li><strong>Technische logs:</strong> Maximaal 90 dagen</li>
          </ul>
          
          <h2 className="text-xl font-semibold text-white mt-8">9. Uw rechten onder de AVG/GDPR</h2>
          <p>U heeft de volgende rechten met betrekking tot uw persoonlijke gegevens:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Recht op inzage:</strong> Opvragen welke gegevens wij van u hebben</li>
            <li><strong>Recht op rectificatie:</strong> Onjuiste gegevens laten corrigeren</li>
            <li><strong>Recht op verwijdering:</strong> Uw gegevens laten verwijderen ("recht om vergeten te worden")</li>
            <li><strong>Recht op beperking:</strong> Verwerking tijdelijk stopzetten</li>
            <li><strong>Recht op overdraagbaarheid:</strong> Uw gegevens in een gestructureerd formaat ontvangen</li>
            <li><strong>Recht van bezwaar:</strong> Bezwaar maken tegen bepaalde verwerkingen</li>
            <li><strong>Recht om toestemming in te trekken:</strong> Op elk moment, zonder gevolgen</li>
          </ul>
          <p>Om uw rechten uit te oefenen, neem contact op via het support systeem in de app. Wij reageren binnen 30 dagen.</p>
          
          <h2 className="text-xl font-semibold text-white mt-8">10. Cookies en tracking</h2>
          <p>Wij gebruiken alleen <strong>strikt noodzakelijke cookies</strong>:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Authenticatie cookies:</strong> Om u ingelogd te houden</li>
            <li><strong>Voorkeur cookies:</strong> Om uw taalinstellingen te onthouden</li>
          </ul>
          <p>Wij gebruiken GEEN tracking cookies, advertentie cookies of analytics van derden die uw gedrag volgen.</p>
          
          <h2 className="text-xl font-semibold text-white mt-8">11. Beveiliging</h2>
          <p>Wij nemen de beveiliging van uw gegevens serieus:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>HTTPS/TLS versleuteling voor alle verbindingen</li>
            <li>Wachtwoorden worden gehasht met bcrypt</li>
            <li>Beveiligde servers met regelmatige updates</li>
            <li>Beperkte toegang tot persoonlijke gegevens</li>
            <li>Rate limiting tegen misbruik</li>
          </ul>
          
          <h2 className="text-xl font-semibold text-white mt-8">12. Kinderen</h2>
          <p>SmartTube is niet bedoeld voor kinderen onder de 16 jaar. Wij verzamelen niet bewust gegevens van kinderen. Als u vermoedt dat een kind onder de 16 zich heeft geregistreerd, neem dan contact met ons op zodat wij de gegevens kunnen verwijderen.</p>
          
          <h2 className="text-xl font-semibold text-white mt-8">13. Wijzigingen in deze policy</h2>
          <p>Wij kunnen deze privacy policy van tijd tot tijd bijwerken. Bij significante wijzigingen zullen wij u per e-mail informeren. De datum "Laatst bijgewerkt" bovenaan geeft aan wanneer de laatste wijziging is doorgevoerd.</p>
          
          <h2 className="text-xl font-semibold text-white mt-8">14. Klachten</h2>
          <p>Als u een klacht heeft over hoe wij met uw gegevens omgaan, kunt u contact opnemen met:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>SmartTube: via het support systeem in de app</li>
            <li>Autoriteit Persoonsgegevens (Nederland): <a href="https://autoriteitpersoonsgegevens.nl" className="text-red-500" target="_blank" rel="noopener noreferrer">autoriteitpersoonsgegevens.nl</a></li>
          </ul>
          
          <h2 className="text-xl font-semibold text-white mt-8">15. Contact</h2>
          <div className="bg-white/5 p-4 rounded-lg mt-4">
            <p><strong>SmartTube</strong></p>
            <p>Contact: via het support systeem in de app</p>
            <p>Website: <a href="https://smarttube.online" className="text-red-500">smarttube.online</a></p>
          </div>
        </div>
      </main>
    </div>
  );
};

// ============== ALGEMENE VOORWAARDEN PAGE ==============
const TermsPage = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <header className="border-b border-white/10 py-4">
        <div className="max-w-4xl mx-auto px-6 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="text-2xl font-bold">
            <span className="text-red-600">Smart</span>Tube
          </button>
          <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-white">← Terug</button>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold mb-8">Algemene Voorwaarden</h1>
        <div className="prose prose-invert max-w-none space-y-6 text-gray-300">
          <p><strong>Laatst bijgewerkt:</strong> Juli 2025</p>
          
          <h2 className="text-xl font-semibold text-white mt-8">1. Definities</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>SmartTube:</strong> Het platform beschikbaar op smarttube.nl</li>
            <li><strong>Gebruiker:</strong> Iedereen die gebruik maakt van SmartTube</li>
            <li><strong>Premium:</strong> Betaald abonnement met extra functies</li>
          </ul>
          
          <h2 className="text-xl font-semibold text-white mt-8">2. Diensten</h2>
          <p>SmartTube biedt een video discovery en curatie platform met gecureerde YouTube content. Video's worden weergegeven via de officiële YouTube IFrame Player API.</p>
          <p><strong>Belangrijk:</strong> Wij zijn geen eigenaar van de getoonde video's. Alle video content blijft eigendom van de respectievelijke YouTube creators en YouTube LLC.</p>
          
          <h2 className="text-xl font-semibold text-white mt-8">3. YouTube Content</h2>
          <p>Door gebruik te maken van SmartTube gaat u akkoord met:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><a href="https://www.youtube.com/t/terms" className="text-red-500" target="_blank" rel="noopener noreferrer">YouTube's Servicevoorwaarden</a></li>
            <li><a href="https://policies.google.com/privacy" className="text-red-500" target="_blank" rel="noopener noreferrer">Google's Privacybeleid</a></li>
          </ul>
          <p>YouTube kan advertenties tonen binnen embedded video's. SmartTube heeft geen controle over het advertentiebeleid van YouTube.</p>
          
          <h2 className="text-xl font-semibold text-white mt-8">4. Account</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>U moet minimaal 16 jaar oud zijn om een account aan te maken</li>
            <li>U bent verantwoordelijk voor het geheim houden van uw wachtwoord</li>
            <li>U mag slechts één account per persoon hebben</li>
          </ul>
          
          <h2 className="text-xl font-semibold text-white mt-8">4. Premium Abonnement</h2>
          <div className="bg-[#141414] p-4 rounded-lg border border-white/10">
            <p className="text-white font-semibold mb-2">⚡ Automatisch Doorlopend Abonnement</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Maandelijks:</strong> €4,99 per maand - wordt automatisch verlengd</li>
              <li><strong>Jaarlijks:</strong> €69,99 per jaar - wordt automatisch verlengd</li>
              <li>U kunt op elk moment opzeggen via uw account instellingen</li>
              <li>Bij opzegging blijft uw Premium actief tot het einde van de betaalde periode</li>
            </ul>
          </div>
          
          <h2 className="text-xl font-semibold text-white mt-8">5. Betaling</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>Betalingen worden verwerkt via Stripe</li>
            <li>Beschikbare methodes: iDEAL, creditcard</li>
            <li>Prijzen zijn inclusief BTW</li>
          </ul>
          
          <h2 className="text-xl font-semibold text-white mt-8">6. Opzeggen</h2>
          <p>U kunt uw Premium abonnement op elk moment opzeggen:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Via uw account instellingen</li>
            <li>Door een bericht te sturen via het support systeem in de app</li>
          </ul>
          <p>Na opzegging blijft uw Premium actief tot het einde van de huidige betaalperiode. Er vindt geen restitutie plaats voor ongebruikte dagen.</p>
          
          <h2 className="text-xl font-semibold text-white mt-8">7. Herroepingsrecht</h2>
          <p>Conform de Wet koop op afstand heeft u 14 dagen bedenktijd. Als u binnen deze periode uw abonnement wilt annuleren en nog geen gebruik heeft gemaakt van de Premium functies, kunt u een volledige restitutie aanvragen.</p>
          
          <h2 className="text-xl font-semibold text-white mt-8">8. Aansprakelijkheid</h2>
          <p>SmartTube is niet aansprakelijk voor:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>De inhoud van video's op YouTube</li>
            <li>Tijdelijke onbeschikbaarheid van de dienst</li>
            <li>Wijzigingen in beschikbare content</li>
          </ul>
          
          <h2 className="text-xl font-semibold text-white mt-8">9. Wijzigingen</h2>
          <p>Wij behouden het recht om deze voorwaarden te wijzigen. Bij belangrijke wijzigingen informeren wij u per e-mail.</p>
          
          <h2 className="text-xl font-semibold text-white mt-8">10. Contact</h2>
          <p>Voor vragen over deze voorwaarden kunt u contact opnemen via het support systeem in de app.</p>
          
          <h2 className="text-xl font-semibold text-white mt-8">11. Toepasselijk recht</h2>
          <p>Op deze voorwaarden is Nederlands recht van toepassing. Geschillen worden voorgelegd aan de bevoegde rechter in Nederland.</p>
        </div>
      </main>
    </div>
  );
};

// ============== SUPPORT PAGE ==============
const SupportPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newTicket, setNewTicket] = useState({ subject: '', message: '', category: 'general' });
  const [replyMessage, setReplyMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/support/tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setTickets(data.tickets || []);
    } catch (err) {
      console.error('Error fetching tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    if (!newTicket.subject.trim() || !newTicket.message.trim()) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/support/tickets`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(newTicket)
      });
      
      if (res.ok) {
        setNewTicket({ subject: '', message: '', category: 'general' });
        setShowNewTicket(false);
        fetchTickets();
      }
    } catch (err) {
      console.error('Error creating ticket:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async (ticketId) => {
    if (!replyMessage.trim()) return;
    
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/api/support/tickets/${ticketId}/reply`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ message: replyMessage })
      });
      
      setReplyMessage('');
      fetchTickets();
      // Refresh selected ticket
      const updated = tickets.find(t => t.id === ticketId);
      if (updated) {
        const res = await fetch(`${API}/api/support/tickets/${ticketId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        setSelectedTicket(data);
      }
    } catch (err) {
      console.error('Error replying:', err);
    }
  };

  const statusColors = {
    open: 'bg-yellow-500/20 text-yellow-400',
    in_progress: 'bg-blue-500/20 text-blue-400',
    resolved: 'bg-green-500/20 text-green-400',
    closed: 'bg-gray-500/20 text-gray-400'
  };

  const statusLabels = {
    open: 'Open',
    in_progress: 'In Behandeling',
    resolved: 'Opgelost',
    closed: 'Gesloten'
  };

  const categoryLabels = {
    general: 'Algemeen',
    bug: 'Bug/Probleem',
    payment: 'Betaling',
    account: 'Account',
    feature: 'Feature Verzoek'
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <header className="border-b border-white/10 py-4">
        <div className="max-w-4xl mx-auto px-6 flex items-center justify-between">
          <button onClick={() => navigate("/home")} className="text-2xl font-bold">
            <span className="text-red-600">Smart</span>Tube
          </button>
          <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-white flex items-center gap-2">
            <ArrowLeft size={18} /> Terug
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">Support</h1>
            <p className="text-gray-400 mt-1">Heb je een vraag of probleem? Wij helpen je graag!</p>
          </div>
          <button 
            onClick={() => setShowNewTicket(true)}
            className="px-6 py-3 bg-red-600 hover:bg-red-700 rounded-full font-semibold transition flex items-center gap-2"
          >
            <MessageSquare size={18} /> Nieuw Bericht
          </button>
        </div>

        {/* New Ticket Modal */}
        {showNewTicket && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-[#141414] rounded-2xl p-6 max-w-lg w-full border border-white/10">
              <h2 className="text-xl font-bold mb-4">Nieuw Support Bericht</h2>
              <form onSubmit={handleSubmitTicket}>
                <div className="mb-4">
                  <label className="block text-sm text-gray-400 mb-2">Categorie</label>
                  <select 
                    value={newTicket.category}
                    onChange={(e) => setNewTicket({...newTicket, category: e.target.value})}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 focus:border-red-500 outline-none"
                  >
                    <option value="general">Algemeen</option>
                    <option value="bug">Bug/Probleem</option>
                    <option value="payment">Betaling</option>
                    <option value="account">Account</option>
                    <option value="feature">Feature Verzoek</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm text-gray-400 mb-2">Onderwerp</label>
                  <input 
                    type="text"
                    value={newTicket.subject}
                    onChange={(e) => setNewTicket({...newTicket, subject: e.target.value})}
                    placeholder="Kort onderwerp..."
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 focus:border-red-500 outline-none"
                    required
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-sm text-gray-400 mb-2">Bericht</label>
                  <textarea 
                    value={newTicket.message}
                    onChange={(e) => setNewTicket({...newTicket, message: e.target.value})}
                    placeholder="Beschrijf je vraag of probleem..."
                    rows={5}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 focus:border-red-500 outline-none resize-none"
                    required
                  />
                </div>
                <div className="flex gap-3">
                  <button 
                    type="button"
                    onClick={() => setShowNewTicket(false)}
                    className="flex-1 py-3 bg-white/10 hover:bg-white/20 rounded-full font-medium transition"
                  >
                    Annuleren
                  </button>
                  <button 
                    type="submit"
                    disabled={submitting}
                    className="flex-1 py-3 bg-red-600 hover:bg-red-700 rounded-full font-semibold transition disabled:opacity-50"
                  >
                    {submitting ? 'Versturen...' : 'Versturen'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Ticket Detail Modal */}
        {selectedTicket && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-[#141414] rounded-2xl p-6 max-w-2xl w-full border border-white/10 max-h-[80vh] overflow-y-auto">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <span className={`text-xs px-2 py-1 rounded-full ${statusColors[selectedTicket.status]}`}>
                    {statusLabels[selectedTicket.status]}
                  </span>
                  <h2 className="text-xl font-bold mt-2">{selectedTicket.subject}</h2>
                  <p className="text-sm text-gray-400">
                    {categoryLabels[selectedTicket.category]} • {new Date(selectedTicket.created_at).toLocaleDateString('nl-NL')}
                  </p>
                </div>
                <button onClick={() => setSelectedTicket(null)} className="text-gray-400 hover:text-white">
                  <X size={24} />
                </button>
              </div>
              
              {/* Original Message */}
              <div className="bg-white/5 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-400 mb-1">Jouw bericht:</p>
                <p className="text-white whitespace-pre-wrap">{selectedTicket.message}</p>
              </div>
              
              {/* Replies */}
              {selectedTicket.replies && selectedTicket.replies.length > 0 && (
                <div className="space-y-3 mb-4">
                  {selectedTicket.replies.map((reply, idx) => (
                    <div key={idx} className={`p-4 rounded-lg ${reply.from_admin ? 'bg-red-600/10 border border-red-600/30' : 'bg-white/5'}`}>
                      <p className="text-sm text-gray-400 mb-1">
                        {reply.from_admin ? `${reply.admin_name} (Admin)` : 'Jij'} • {new Date(reply.created_at).toLocaleDateString('nl-NL')}
                      </p>
                      <p className="text-white whitespace-pre-wrap">{reply.message}</p>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Reply Form */}
              {selectedTicket.status !== 'closed' && (
                <div className="border-t border-white/10 pt-4">
                  <textarea 
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    placeholder="Schrijf een reactie..."
                    rows={3}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 focus:border-red-500 outline-none resize-none mb-3"
                  />
                  <button 
                    onClick={() => handleReply(selectedTicket.id)}
                    disabled={!replyMessage.trim()}
                    className="px-6 py-2 bg-red-600 hover:bg-red-700 rounded-full font-medium transition disabled:opacity-50"
                  >
                    Verstuur Reactie
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tickets List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="animate-spin text-red-500" size={32} />
          </div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-12 bg-[#141414] rounded-2xl border border-white/10">
            <MessageSquare className="mx-auto text-gray-600 mb-4" size={48} />
            <h3 className="text-xl font-semibold mb-2">Geen berichten</h3>
            <p className="text-gray-400">Je hebt nog geen support berichten verstuurd.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {tickets.map(ticket => (
              <div 
                key={ticket.id}
                onClick={() => setSelectedTicket(ticket)}
                className="bg-[#141414] border border-white/10 rounded-xl p-4 hover:border-red-600/50 cursor-pointer transition"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[ticket.status]}`}>
                        {statusLabels[ticket.status]}
                      </span>
                      <span className="text-xs text-gray-500">{categoryLabels[ticket.category]}</span>
                    </div>
                    <h3 className="font-semibold">{ticket.subject}</h3>
                    <p className="text-sm text-gray-400 line-clamp-1">{ticket.message}</p>
                  </div>
                  <div className="text-right text-sm text-gray-500">
                    <p>{new Date(ticket.created_at).toLocaleDateString('nl-NL')}</p>
                    {ticket.replies && ticket.replies.length > 0 && (
                      <p className="text-red-400">{ticket.replies.length} reactie(s)</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};


// ============== COOKIE BANNER ==============
const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  
  useEffect(() => {
    const accepted = localStorage.getItem('cookies_accepted');
    if (!accepted) {
      setVisible(true);
    }
  }, []);
  
  const acceptCookies = () => {
    localStorage.setItem('cookies_accepted', 'true');
    setVisible(false);
  };
  
  if (!visible) return null;
  
  return (
    <div className="fixed bottom-0 left-0 right-0 bg-[#141414] border-t border-white/10 p-4 z-50">
      <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-gray-300 text-sm text-center sm:text-left">
          🍪 Wij gebruiken functionele cookies om de website goed te laten werken en uw voorkeuren te onthouden. 
          <a href="/privacy" className="text-red-500 hover:underline ml-1">Meer info</a>
        </p>
        <button 
          onClick={acceptCookies}
          className="px-6 py-2 bg-red-600 hover:bg-red-700 rounded-full text-white text-sm font-medium whitespace-nowrap transition"
        >
          Accepteren
        </button>
      </div>
    </div>
  );
};

// ============== MAIN APP ==============
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/home" element={<ProtectedRoute><SmartTubeHome /></ProtectedRoute>} />
          <Route path="/premium" element={<ProtectedRoute><PremiumPage /></ProtectedRoute>} />
          <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
          <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute ownerOnly><AdminPage /></ProtectedRoute>} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/voorwaarden" element={<TermsPage />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        <CookieBanner />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
